/**
 * Hot Bags — Live Feed
 * Airbnb-style split view with Leaflet map, auto-scrolling columns, and pin popups
 */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     City-to-Coordinates Fallback Lookup
     -------------------------------------------------------------------------- */

  var CITY_COORDS = {
    'hong kong': { lat: 22.3193, lng: 114.1694 },
    'tokyo': { lat: 35.6762, lng: 139.6503 },
    'paris': { lat: 48.8566, lng: 2.3522 },
    'london': { lat: 51.5074, lng: -0.1278 },
    'dubai': { lat: 25.2048, lng: 55.2708 },
    'new york': { lat: 40.7128, lng: -74.006 },
    'milan': { lat: 45.4642, lng: 9.19 },
    'singapore': { lat: 1.3521, lng: 103.8198 },
    'los angeles': { lat: 34.0522, lng: -118.2437 },
    'miami': { lat: 25.7617, lng: -80.1918 },
    'seoul': { lat: 37.5665, lng: 126.978 },
    'shanghai': { lat: 31.2304, lng: 121.4737 },
    'beijing': { lat: 39.9042, lng: 116.4074 },
    'bangkok': { lat: 13.7563, lng: 100.5018 },
    'sydney': { lat: -33.8688, lng: 151.2093 },
    'melbourne': { lat: -37.8136, lng: 144.9631 },
    'zurich': { lat: 47.3769, lng: 8.5417 },
    'geneva': { lat: 46.2044, lng: 6.1432 },
    'rome': { lat: 41.9028, lng: 12.4964 },
    'munich': { lat: 48.1351, lng: 11.582 },
    'amsterdam': { lat: 52.3676, lng: 4.9041 },
    'istanbul': { lat: 41.0082, lng: 28.9784 },
    'riyadh': { lat: 24.7136, lng: 46.6753 },
    'doha': { lat: 25.2854, lng: 51.531 },
    'abu dhabi': { lat: 24.4539, lng: 54.3773 },
    'kuala lumpur': { lat: 3.139, lng: 101.6869 },
    'taipei': { lat: 25.033, lng: 121.5654 },
    'osaka': { lat: 34.6937, lng: 135.5023 },
    'las vegas': { lat: 36.1699, lng: -115.1398 },
    'monaco': { lat: 43.7384, lng: 7.4246 },
    'vancouver': { lat: 49.2827, lng: -123.1207 },
    'toronto': { lat: 43.6532, lng: -79.3832 },
    'berlin': { lat: 52.52, lng: 13.405 },
    'madrid': { lat: 40.4168, lng: -3.7038 },
    'lisbon': { lat: 38.7223, lng: -9.1393 },
    'moscow': { lat: 55.7558, lng: 37.6173 },
    'mumbai': { lat: 19.076, lng: 72.8777 },
    'jakarta': { lat: -6.2088, lng: 106.8456 }
  };

  function lookupCity(cityName) {
    if (!cityName) return null;
    return CITY_COORDS[cityName.trim().toLowerCase()] || null;
  }

  /* --------------------------------------------------------------------------
     Customs Duty Calculator

     Assumptions baked into this calculator (review whenever stock expands):
       - All listed bags are Hermès Birkin / Kelly (non-exotic leather).
         HS code = 4202.21.00 — exotics/CITES items are out of scope.
       - "Origin" for duty-rate purposes is the bag's current physical
         location (bag_location_country metafield). This reflects where it
         will ship from, which is what the buyer's customs agent sees on
         the import paperwork. If you want to claim preferential EU-origin
         treatment for a French-made bag consigned outside the EU, that
         requires a re-export certificate — out of scope for the estimator.
       - VAT/GST rates are as of April 2026. Flag for review every ~6 months.
       - No de-minimis thresholds, no state/provincial sales tax (US/CA), no
         brokerage fees, no anti-dumping duties are modelled. Figures are
         indicative, not a landed-cost quote.
     -------------------------------------------------------------------------- */

  // EU customs territory — MC (Monaco) uses the French customs system and
  // is treated as EU for duty/VAT purposes.
  var EU_ISO = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','MC','NL','PL','PT','RO','SK','SI','ES','SE'];
  var GCC_ISO = ['AE','SA','KW','BH','QA','OM'];

  // Map country names (as stored in metafield) to ISO codes
  var COUNTRY_NAME_ISO = {
    'france':'FR','germany':'DE','italy':'IT','spain':'ES','portugal':'PT',
    'netherlands':'NL','belgium':'BE','austria':'AT','switzerland':'CH',
    'united kingdom':'GB','uk':'GB','england':'GB','great britain':'GB',
    'united states':'US','usa':'US','america':'US','united states of america':'US',
    'hong kong':'HK','singapore':'SG','japan':'JP',
    'south korea':'KR','korea':'KR',
    'australia':'AU','canada':'CA','china':'CN','taiwan':'TW',
    'uae':'AE','dubai':'AE','united arab emirates':'AE','abu dhabi':'AE',
    'saudi arabia':'SA','kuwait':'KW','qatar':'QA','bahrain':'BH','oman':'OM',
    'sweden':'SE','denmark':'DK','norway':'NO','finland':'FI','ireland':'IE',
    'greece':'GR','poland':'PL','czech republic':'CZ','czechia':'CZ',
    'hungary':'HU','romania':'RO','bulgaria':'BG','croatia':'HR',
    'slovakia':'SK','slovenia':'SI','estonia':'EE','latvia':'LV','lithuania':'LT',
    'luxembourg':'LU','malta':'MT','cyprus':'CY','monaco':'MC',
    'russia':'RU','brazil':'BR','mexico':'MX','india':'IN','turkey':'TR',
    'indonesia':'ID','malaysia':'MY','thailand':'TH','new zealand':'NZ',
    'south africa':'ZA','vietnam':'VN','philippines':'PH','pakistan':'PK'
  };

  // Per-destination rates for HS 4202.21 (leather handbags).
  //   dutyFromEU    = rate when the bag is shipping from an EU member state
  //                   (preferential under EU FTAs).
  //   dutyFromNonEU = MFN/standard rate — applied when the bag is shipping
  //                   from a non-EU origin.
  //   vat           = destination VAT / GST / sales tax (federal only).
  var DEST_RATES = {
    // EU member states — intra-EU sales have no customs event.
    // MFN for HS 4202.21 is 3% (not 3.7% — that's the blended 4202.22 rate).
    AT:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.20},
    BE:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.21},
    BG:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.20},
    HR:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.25},
    CY:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.19},
    CZ:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.21},
    DK:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.25},
    EE:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.24}, // raised from 22% → 24% Jul 2025
    FI:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.255},// raised from 24% → 25.5% Sep 2024
    FR:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.20},
    DE:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.19},
    GR:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.24},
    HU:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.27},
    IE:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.23},
    IT:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.22},
    LV:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.21},
    LT:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.21},
    LU:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.17},
    MT:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.18},
    MC:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.20}, // French customs/VAT system
    NL:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.21},
    PL:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.23},
    PT:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.23},
    RO:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.19},
    SK:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.23}, // raised from 20% → 23% Jan 2025
    SI:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.22},
    ES:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.21},
    SE:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.25},
    // UK — TCA: 0% on EU-origin goods with proof of origin, 3% MFN otherwise
    GB:{dutyFromEU:0,   dutyFromNonEU:0.03, vat:0.20},
    // Switzerland — industrial tariffs abolished Jan 2024
    CH:{dutyFromEU:0,   dutyFromNonEU:0,    vat:0.081},
    // Norway — EEA; duty-free from EU, 25% VAT
    NO:{dutyFromEU:0,   dutyFromNonEU:0.07, vat:0.25},
    // Turkey — EU-TR customs union covers industrial goods incl. 4202.21
    TR:{dutyFromEU:0,   dutyFromNonEU:0.18, vat:0.20},
    // EU-Mexico Global Agreement: 0% from EU, IVA 16%
    MX:{dutyFromEU:0,   dutyFromNonEU:0.15, vat:0.16},
    // Free ports / zero duty regardless of origin
    HK:{dutyFromEU:0,   dutyFromNonEU:0,    vat:0},
    SG:{dutyFromEU:0,   dutyFromNonEU:0,    vat:0.09},
    // EU-Japan EPA — handbag tariffs fully eliminated Feb 2030; as of 2026
    // residual ~1.3% remains. Rounded to 0.015 for the estimator.
    JP:{dutyFromEU:0.015,dutyFromNonEU:0.08,vat:0.10},
    // EU-Korea FTA: 0% from EU, 8% MFN
    KR:{dutyFromEU:0,   dutyFromNonEU:0.08, vat:0.10},
    // CETA: 0% from EU, 10% MFN
    CA:{dutyFromEU:0,   dutyFromNonEU:0.10, vat:0.05},
    // No EU FTA — flat rates apply regardless of origin.
    AU:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0.10},
    NZ:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0.15},
    // GCC common external tariff — 5% on all origins, VAT varies by member.
    AE:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0.05},
    SA:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0.15},
    KW:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0},
    QA:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0},    // no VAT in Qatar
    BH:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0.10},
    OM:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0.05},
    // Taiwan — MFN for 4202.21 is ~5%
    TW:{dutyFromEU:0.05,dutyFromNonEU:0.05, vat:0.05},
    // China — 10% import duty on luxury leather goods + 13% VAT.
    // (Excludes consumption tax on high-value items > CNY 10k, which is
    // separate and rarely enforced on personal consignments.)
    CN:{dutyFromEU:0.10,dutyFromNonEU:0.10, vat:0.13},
    // US — tariff regime is politically volatile. As of Apr 2026 the EU
    // reciprocal tariff on luxury leather goods sits at ~15%. Review
    // quarterly. No federal VAT; state sales tax not modelled.
    US:{dutyFromEU:0.15,dutyFromNonEU:0.15, vat:0},
    // Fallback for destinations we haven't researched. Conservative midpoint.
    _DEFAULT:{dutyFromEU:0.05,dutyFromNonEU:0.05,vat:0.20}
  };

  var detectedISO     = null;  // IP-detected country
  var selectedDestISO = null;  // user-selected (may differ from detected)
  var moneyFormat     = '{{amount}}';
  var pickerInstances = [];
  var qvCustomsRenderer = null;
  var qvCartPropsRenderer = null;
  var currentQvPicker   = null;
  var mapInstance       = null;

  var ISO_NAMES = {
    AT:'Austria', BE:'Belgium', BG:'Bulgaria', HR:'Croatia', CY:'Cyprus',
    CZ:'Czech Republic', DK:'Denmark', EE:'Estonia', FI:'Finland', FR:'France',
    DE:'Germany', GR:'Greece', HU:'Hungary', IE:'Ireland', IT:'Italy',
    LV:'Latvia', LT:'Lithuania', LU:'Luxembourg', MT:'Malta', MC:'Monaco',
    NL:'Netherlands', PL:'Poland', PT:'Portugal', RO:'Romania', SK:'Slovakia',
    SI:'Slovenia', ES:'Spain', SE:'Sweden',
    GB:'United Kingdom', CH:'Switzerland', NO:'Norway', TR:'Turkey',
    US:'United States', CA:'Canada', MX:'Mexico',
    HK:'Hong Kong', SG:'Singapore', JP:'Japan', KR:'South Korea',
    AU:'Australia', NZ:'New Zealand', TW:'Taiwan', CN:'China',
    AE:'UAE', SA:'Saudi Arabia', KW:'Kuwait', QA:'Qatar', BH:'Bahrain', OM:'Oman'
  };

  /* Approximate country centroids — used to pan/center the map on the user's
     selected or detected country without aggressive zoom. */
  var COUNTRY_CENTERS = {
    AT:[47.52,14.55], BE:[50.64,4.67],  BG:[42.73,25.49], HR:[45.10,15.20], CY:[35.13,33.43],
    CZ:[49.82,15.47], DK:[56.26,9.50],  EE:[58.60,25.01], FI:[64.00,26.00], FR:[46.20,2.21],
    DE:[51.17,10.45], GR:[39.07,21.82], HU:[47.16,19.50], IE:[53.41,-8.24], IT:[41.87,12.57],
    LV:[56.88,24.60], LT:[55.17,23.88], LU:[49.82,6.13],  MT:[35.94,14.38], MC:[43.74,7.42],
    NL:[52.13,5.29],  PL:[51.92,19.15], PT:[39.40,-8.22], RO:[45.94,24.97], SK:[48.67,19.70],
    SI:[46.15,14.99], ES:[40.46,-3.75], SE:[60.13,18.64],
    GB:[54.00,-2.50], CH:[46.82,8.23],  NO:[60.47,8.47],  TR:[38.96,35.24],
    US:[39.50,-98.35],CA:[56.13,-106.35],MX:[23.63,-102.55],
    HK:[22.32,114.17],SG:[1.35,103.82], JP:[36.20,138.25],KR:[35.91,127.77],
    AU:[-25.27,133.77],NZ:[-41.29,174.78],TW:[23.70,120.96],CN:[35.86,104.20],
    AE:[23.42,53.85], SA:[23.89,45.08], KW:[29.31,47.48], QA:[25.35,51.18], BH:[26.07,50.56], OM:[21.47,55.97]
  };

  /* Zoom level used when panning to a country centroid — country-scale,
     not aggressive. Mobile is slightly lower so the country fits the narrower viewport. */
  function countryZoom() {
    return window.innerWidth < 1000 ? 3 : 3.5;
  }

  var COUNTRY_GROUPS = [
    { label: 'Europe',       isos: ['GB','CH','NO','TR','AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','MC','NL','PL','PT','RO','SK','SI','ES','SE'] },
    { label: 'Americas',     isos: ['US','CA','MX'] },
    { label: 'Asia Pacific', isos: ['HK','SG','JP','KR','AU','NZ','TW','CN'] },
    { label: 'Middle East',  isos: ['AE','SA','KW','QA','BH','OM'] }
  ];

  // Non-ISO 2-letter codes that appear in merchant metafields. Normalised to
  // the canonical ISO 3166-1 alpha-2 code so duty/VAT lookups succeed.
  var ISO_ALIASES = {
    UK: 'GB', EN: 'GB', GBR: 'GB',
    UAE: 'AE', KSA: 'SA',
    USA: 'US', AMR: 'US',
    PRC: 'CN', ROC: 'TW'
  };

  function countryRawToISO(raw) {
    if (!raw) return null;
    var trimmed = raw.trim();
    if (!trimmed) return null;
    var upper = trimmed.toUpperCase();
    if (ISO_ALIASES[upper]) return ISO_ALIASES[upper];
    if (trimmed.length === 2) return upper;
    return COUNTRY_NAME_ISO[trimmed.toLowerCase()] || null;
  }

  /**
   * Estimate landed-cost uplift for a Hermès bag shipping internationally.
   *
   * @param {number} priceMinorUnits   Sale price in the store's minor units (pennies).
   * @param {string} userISO           Destination country (buyer) — ISO alpha-2.
   * @param {string} bagISO            Country the bag is currently located in,
   *                                   which also serves as its origin for
   *                                   customs-rate purposes — ISO alpha-2.
   * @returns {object|null}            Null if no customs event occurs (domestic
   *                                   sale, or both parties in the same customs
   *                                   zone). Otherwise the duty/VAT breakdown.
   */
  function calcDuty(priceMinorUnits, userISO, bagISO) {
    if (!userISO || !bagISO) return null;

    var userIsEU = EU_ISO.indexOf(userISO) !== -1;
    var bagIsEU  = EU_ISO.indexOf(bagISO) !== -1;

    // No customs event cases:
    //   1. Domestic sale — bag is already in the buyer's country.
    //   2. Intra-EU sale — free movement of goods, no border.
    //   3. Intra-GCC sale — unified customs union.
    if (userISO === bagISO) return null;
    if (userIsEU && bagIsEU) return null;
    if (GCC_ISO.indexOf(userISO) !== -1 && GCC_ISO.indexOf(bagISO) !== -1) return null;

    // Border-crossing shipment. Pick the EU-preferential rate when the bag is
    // physically in the EU, otherwise the MFN rate.
    var rates = DEST_RATES[userISO] || (userIsEU ? DEST_RATES.FR : DEST_RATES._DEFAULT);
    var dutyRate = bagIsEU ? rates.dutyFromEU : rates.dutyFromNonEU;
    var vatRate  = rates.vat;
    var price    = priceMinorUnits / 100;
    var dutyAmt  = price * dutyRate;
    var vatAmt   = (price + dutyAmt) * vatRate;
    var total    = dutyAmt + vatAmt;

    var totalRate = price > 0 ? total / price : 0;
    var tier;
    if (totalRate === 0)        tier = 'none';
    else if (totalRate <= 0.08) tier = 'low';
    else if (totalRate <= 0.20) tier = 'medium';
    else                        tier = 'high';

    return { dutyRate:dutyRate, vatRate:vatRate, dutyAmt:dutyAmt, vatAmt:vatAmt, total:total, tier:tier };
  }

  function fmt(minorUnits) {
    var amount = (minorUnits / 100).toFixed(2).replace(/\.00$/, '');
    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(minorUnits / 100))
      .replace('{{amount_with_comma_separator}}', (minorUnits / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  }

  function pct(rate) { return Math.round(rate * 100) + '%'; }

  var TIER_LABELS = { none:'No Import Duties', low:'Low Duties', medium:'Medium Duties', high:'High Duties' };

  function buildDutyBadge(tier) {
    var div = document.createElement('div');
    div.className = 'lf-card__duty-badge lf-card__duty-badge--' + tier;
    div.textContent = TIER_LABELS[tier];
    return div;
  }

  /* Renders the customs body rows (used by QV live re-render) */
  function buildCustomsBodyHTML(result) {
    if (!result) {
      return '<p class="lf-qv__customs-note lf-qv__customs-note--free">No import duties — this bag is in your region.</p>';
    }
    var html = '<div class="lf-qv__customs-rows">';
    if (result.dutyRate > 0) {
      html += '<div class="lf-qv__customs-row"><span>Import duty (' + pct(result.dutyRate) + ')</span><span>' + fmt(Math.round(result.dutyAmt * 100)) + '</span></div>';
    } else {
      html += '<div class="lf-qv__customs-row lf-qv__customs-row--nil"><span>Import duty</span><span>None (0%)</span></div>';
    }
    if (result.vatRate > 0) {
      html += '<div class="lf-qv__customs-row"><span>Local VAT/GST (' + pct(result.vatRate) + ')</span><span>' + fmt(Math.round(result.vatAmt * 100)) + '</span></div>';
    }
    html += '</div>';
    html += '<div class="lf-qv__customs-total"><span>Total additional cost</span><span>' + fmt(Math.round(result.total * 100)) + '</span></div>';
    html += '<p class="lf-qv__customs-disclaimer">Estimates for a leather handbag (HS 4202.21) shipping from the bag\'s current location. Excludes carrier fees and state/provincial sales tax. Consult a customs broker for a binding quote.</p>';
    return html;
  }

  /**
   * Add / update / remove a line-item property on a /cart/add form.
   *
   * Shopify stores `properties[X]` hidden inputs as line item properties on
   * the resulting order. Property names starting with `_` are hidden from
   * customer-facing surfaces (email, invoice) but remain visible in admin,
   * webhooks, and Flow — used here for raw numeric values we want available
   * to ops automation without cluttering the customer's invoice.
   *
   * Passing value = null removes the input entirely (so properties only
   * appear on the order when they actually apply — e.g. import duties are
   * omitted for domestic shipping methods).
   */
  function setFormProp(form, propName, value) {
    var name = 'properties[' + propName + ']';
    var input = form.querySelector('input[name="' + name + '"]');

    if (value === null || value === undefined) {
      if (input) input.remove();
      return;
    }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  /**
   * Human-readable shipping method label for the customer-facing `Shipping`
   * line item property. The raw code is also stored as `_shipping_method_code`
   * for automation.
   */
  function shippingMethodLabel(method, bagCity) {
    switch (method) {
      case 'collection':
        return bagCity ? 'Local Collection in ' + bagCity : 'Local Collection';
      case 'white-glove':
        return bagCity ? 'White Glove Local Delivery in ' + bagCity : 'White Glove Local Delivery';
      case 'local':
        return 'Local Delivery';
      case 'international':
      default:
        return 'International Shipping';
    }
  }

  /**
   * Cost (minor units) for each shipping method. Mirrors the prices
   * baked into `theme/snippets/live-feed-shipping-card.liquid`:
   * local options are free; International is the £200 flat rate.
   * The shop is GBP-only on this surface so the literal pence value
   * doesn't need to vary by currency.
   */
  function shippingCostMinor(method) {
    return method === 'international' ? 20000 : 0;
  }

  function injectDutyBadges() {
    var destISO = selectedDestISO;
    if (!destISO) return;
    document.querySelectorAll('.lf-card').forEach(function (card) {
      var old = card.querySelector('.lf-card__duty-badge');
      if (old) old.remove();
      // Exotic bags never get a traffic-light badge — they can't ship
      // internationally, so a destination-country duty tier is meaningless.
      if (card.getAttribute('data-exotic') === 'true') return;
      var bagISO = countryRawToISO(card.getAttribute('data-country'));
      var price  = parseInt(card.getAttribute('data-price'), 10);
      if (!bagISO || isNaN(price)) return;
      var result = calcDuty(price, destISO, bagISO);
      if (result === null) return;
      var badge = buildDutyBadge(result.tier);
      var info  = card.querySelector('.lf-card__info');
      if (info) info.appendChild(badge);
    });
  }

  function onDestChange(iso) {
    selectedDestISO = iso;
    try { localStorage.setItem('lf-dest-iso', iso); } catch (e) {}
    injectDutyBadges();
    pickerInstances.forEach(function (p) { p.sync(iso); });
    if (qvCustomsRenderer) qvCustomsRenderer(iso);
    if (qvCartPropsRenderer) qvCartPropsRenderer(iso);
    if (mapInstance) {
      mapInstance.updatePinColors(iso);
      mapInstance.flyToCountry(iso);
    }
  }

  /**
   * Resolve the user's country ISO, using:
   *   1. A server-rendered fallback on the given element (data-user-country)
   *   2. localStorage (`lf-dest-iso`) for repeat visitors
   *   3. ipapi.co async lookup
   *
   * Calls `afterDetect(iso)` once a valid code is resolved. Can be called
   * safely on any page that needs duty calculations (Trading Floor collection
   * via `#lf-split`, Hot Bags PDP via `[data-hotbag-pdp]`, etc.) — the caller
   * wires its own side-effects.
   */
  function detectUserCountry(rootEl, afterDetect) {
    var domCountry = ((rootEl && rootEl.getAttribute('data-user-country')) || '').trim();
    if (domCountry.length === 2) { afterDetect(domCountry.toUpperCase()); return; }

    fetch('https://ipapi.co/country/')
      .then(function (r) { return r.text(); })
      .then(function (iso) {
        iso = iso.trim().toUpperCase();
        if (/^[A-Z]{2}$/.test(iso)) afterDetect(iso);
      })
      .catch(function () {});
  }

  function initCustomsDuty() {
    var splitEl = document.getElementById('lf-split');
    if (!splitEl) return;
    moneyFormat = splitEl.getAttribute('data-money-format') || '{{amount}}';

    detectUserCountry(splitEl, function (iso) {
      detectedISO = iso;
      var saved = null;
      try { saved = localStorage.getItem('lf-dest-iso'); } catch (e) {}
      selectedDestISO = (saved && ISO_NAMES[saved]) ? saved : iso;
      injectDutyBadges();
      initToolbarPicker();
      // If the map was created before the async country detection finished,
      // pan it to the resolved country now.
      if (mapInstance && selectedDestISO) {
        mapInstance.updatePinColors(selectedDestISO);
        mapInstance.flyToCountry(selectedDestISO);
      }
    });
  }

  /* ── Country picker component ── */
  function createCountryPicker(cfg) {
    // cfg: { showTiers, priceMinorUnits, bagISO, fixed, onChange }
    var open = false;

    var wrap = document.createElement('div');
    wrap.className = 'lf-cpick' + (cfg.showTiers ? ' lf-cpick--tiers' : '') + (cfg.fixed ? ' lf-cpick--fixed' : '');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lf-cpick__btn';

    var dropdown = document.createElement('div');
    dropdown.className = 'lf-cpick__dropdown';

    function getTier(iso) {
      if (!cfg.showTiers || !cfg.bagISO || !cfg.priceMinorUnits) return null;
      var r = calcDuty(cfg.priceMinorUnits, iso, cfg.bagISO);
      return r ? r.tier : null;
    }

    function makeDot(tier) {
      var s = document.createElement('span');
      s.className = 'lf-cpick__dot lf-cpick__dot--' + (tier || 'none');
      return s;
    }

    function renderBtn(iso) {
      iso = iso || selectedDestISO || detectedISO;
      var name = ISO_NAMES[iso] || iso || '—';
      var isDetected = iso === detectedISO;
      var tier = getTier(iso);

      btn.innerHTML = '';

      if (!cfg.showTiers) {
        var lbl = document.createElement('span');
        lbl.className = 'lf-cpick__btn-label';
        lbl.textContent = 'Importing to:';
        btn.appendChild(lbl);
      }

      var row = document.createElement('span');
      row.className = 'lf-cpick__btn-value';
      if (cfg.showTiers) row.appendChild(makeDot(tier));

      var nameSpan = document.createElement('span');
      nameSpan.className = 'lf-cpick__btn-name';
      nameSpan.textContent = name;
      row.appendChild(nameSpan);

      var chev = document.createElement('span');
      chev.className = 'lf-cpick__chevron';
      chev.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M2 3.5l3 3 3-3"/></svg>';
      row.appendChild(chev);
      btn.appendChild(row);

      if (!cfg.showTiers && isDetected) {
        var sub = document.createElement('span');
        sub.className = 'lf-cpick__btn-sub';
        sub.textContent = 'Detected location';
        btn.appendChild(sub);
      }
    }

    function buildOpt(iso, note) {
      var opt = document.createElement('div');
      opt.className = 'lf-cpick__option' + (iso === selectedDestISO ? ' is-selected' : '');
      opt.setAttribute('data-iso', iso);
      if (cfg.showTiers) opt.appendChild(makeDot(getTier(iso)));
      var nm = document.createElement('span');
      nm.className = 'lf-cpick__opt-name';
      nm.textContent = ISO_NAMES[iso] || iso;
      opt.appendChild(nm);
      if (note) {
        var nt = document.createElement('span');
        nt.className = 'lf-cpick__opt-note';
        nt.textContent = note;
        opt.appendChild(nt);
      }
      opt.addEventListener('click', function (e) { e.stopPropagation(); select(iso); });
      return opt;
    }

    function renderDropdown() {
      dropdown.innerHTML = '';
      if (detectedISO && ISO_NAMES[detectedISO]) {
        dropdown.appendChild(buildOpt(detectedISO, 'Detected location'));
        var div = document.createElement('div');
        div.className = 'lf-cpick__divider';
        dropdown.appendChild(div);
      }
      COUNTRY_GROUPS.forEach(function (group) {
        var items = group.isos.filter(function (iso) { return iso !== detectedISO && ISO_NAMES[iso]; });
        if (!items.length) return;
        var grpEl = document.createElement('div');
        grpEl.className = 'lf-cpick__group';
        var grpLbl = document.createElement('div');
        grpLbl.className = 'lf-cpick__group-label';
        grpLbl.textContent = group.label;
        grpEl.appendChild(grpLbl);
        items.forEach(function (iso) { grpEl.appendChild(buildOpt(iso, null)); });
        dropdown.appendChild(grpEl);
      });
    }

    function openDropdown() {
      renderDropdown();
      open = true;
      wrap.classList.add('is-open');

      // Reset inline positioning from any previous open so a viewport
      // resize between opens doesn't leave the dropdown stuck in stale
      // coordinates (e.g. mobile-fixed sizing persisting into a desktop
      // layout).
      dropdown.style.position = '';
      dropdown.style.top = '';
      dropdown.style.bottom = '';
      dropdown.style.left = '';
      dropdown.style.right = '';
      dropdown.style.width = '';
      dropdown.style.minWidth = '';
      dropdown.style.maxWidth = '';
      dropdown.style.maxHeight = '';

      var rect = btn.getBoundingClientRect();
      var viewportH = window.innerHeight;
      var viewportW = window.innerWidth;
      var GAP = 4;
      var MARGIN = 12; // min distance from the viewport edge
      var isMobile = window.matchMedia('(max-width: 699px)').matches;

      // Mobile: anchor the right edge to the button (same as desktop) but
      // clamp the left edge to MARGIN so the dropdown can never spill
      // off-screen left when the picker sits on the left of a wrapped
      // toolbar. Width stays compact rather than spanning the viewport.
      if (isMobile) {
        dropdown.style.position = 'fixed';
        var dropdownW = Math.min(Math.max(rect.width, 220), viewportW - MARGIN * 2);
        dropdown.style.width = dropdownW + 'px';
        dropdown.style.minWidth = '0';
        dropdown.style.maxWidth = 'none';

        // Prefer right-anchored under the button. If that pushes the left
        // edge past the viewport's left margin, shift the dropdown right
        // so the left margin is respected; right edge then sits inside
        // the viewport rather than at the button's right.
        var leftIfRightAnchored = rect.right - dropdownW;
        if (leftIfRightAnchored < MARGIN) {
          dropdown.style.left = MARGIN + 'px';
          dropdown.style.right = 'auto';
        } else {
          dropdown.style.right = (viewportW - rect.right) + 'px';
          dropdown.style.left = 'auto';
        }

        var naturalHMobile = dropdown.offsetHeight;
        var spaceBelowMobile = viewportH - rect.bottom - MARGIN;
        var spaceAboveMobile = rect.top - MARGIN;
        var flipUpMobile = (naturalHMobile + GAP > spaceBelowMobile) && (spaceAboveMobile > spaceBelowMobile);

        if (flipUpMobile) {
          dropdown.style.bottom = (viewportH - rect.top + GAP) + 'px';
          if (naturalHMobile + GAP > spaceAboveMobile) {
            dropdown.style.maxHeight = spaceAboveMobile + 'px';
          }
        } else {
          dropdown.style.top = (rect.bottom + GAP) + 'px';
          if (naturalHMobile + GAP > spaceBelowMobile) {
            dropdown.style.maxHeight = spaceBelowMobile + 'px';
          }
        }
        return;
      }

      // Fixed-variant (quick-view) positioning. We flip above when there's
      // not enough room below — important on mobile where the quick-view
      // customs header can sit near the bottom of the viewport and an
      // unconstrained downward dropdown would spill off-screen.
      if (cfg.fixed) {
        dropdown.style.top = 'auto';
        dropdown.style.bottom = 'auto';
        dropdown.style.right = (viewportW - rect.right) + 'px';
        dropdown.style.left = 'auto';
        dropdown.style.minWidth = Math.max(rect.width, 180) + 'px';

        var naturalH = dropdown.offsetHeight;
        var spaceBelow = viewportH - rect.bottom - MARGIN;
        var spaceAbove = rect.top - MARGIN;

        // Prefer opening downward; flip up if there's clearly more room
        // above OR the dropdown can't fit below without being clipped.
        var flipUp = (naturalH + GAP > spaceBelow) && (spaceAbove > spaceBelow);

        if (flipUp) {
          dropdown.style.bottom = (viewportH - rect.top + GAP) + 'px';
          if (naturalH + GAP > spaceAbove) {
            dropdown.style.maxHeight = spaceAbove + 'px';
          }
        } else {
          dropdown.style.top = (rect.bottom + GAP) + 'px';
          if (naturalH + GAP > spaceBelow) {
            dropdown.style.maxHeight = spaceBelow + 'px';
          }
        }
      }
    }

    function closeDropdown() {
      open = false;
      wrap.classList.remove('is-open');
    }

    function select(iso) {
      closeDropdown();
      if (cfg.onChange) cfg.onChange(iso);
    }

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (open) closeDropdown(); else openDropdown();
    });
    dropdown.addEventListener('click', function (e) { e.stopPropagation(); });

    var docHandler = function () { if (open) closeDropdown(); };
    document.addEventListener('click', docHandler);

    renderBtn();
    wrap.appendChild(btn);
    wrap.appendChild(dropdown);

    var instance = {
      el: wrap,
      sync: function (iso) {
        renderBtn(iso);
        dropdown.querySelectorAll('.lf-cpick__option').forEach(function (opt) {
          opt.classList.toggle('is-selected', opt.getAttribute('data-iso') === iso);
        });
      },
      destroy: function () {
        document.removeEventListener('click', docHandler);
        pickerInstances = pickerInstances.filter(function (p) { return p !== instance; });
      }
    };
    pickerInstances.push(instance);
    return instance;
  }

  function initToolbarPicker() {
    var toolbar = document.querySelector('.lf-toolbar');
    if (!toolbar || !selectedDestISO) return;
    var picker = createCountryPicker({ showTiers: false, onChange: onDestChange });
    toolbar.appendChild(picker.el);
  }

  /* Mobile-only collapse of the count + explain row on scroll-down, restore
     on scroll-up. Inactive ≥1000px — `.is-shrunk` is removed when the
     viewport widens past the breakpoint.

     Two anti-flicker safeguards:
       1. Symmetric hysteresis (`THRESHOLD` px both directions) so small
          micro-bobbles from touch overshoot don't toggle state.
       2. A `lockedUntil` window equal to the CSS transition duration. The
          row's max-height transition changes document height for ~280ms;
          mobile browsers respond with scroll anchoring (adjusting scrollY
          to keep visible content stable) which would otherwise trip the
          listener in the opposite direction and start a feedback loop. */
  function initToolbarCollapse() {
    var toolbar = document.querySelector('.lf-toolbar');
    if (!toolbar) return;

    var mql = window.matchMedia('(max-width: 999px)');
    var THRESHOLD = 8;
    var TRANSITION_LOCK_MS = 320;
    var lastY = Math.max(0, window.scrollY);
    var lockedUntil = 0;
    var ticking = false;

    function update() {
      ticking = false;
      if (!mql.matches) {
        toolbar.classList.remove('is-shrunk');
        return;
      }
      var y = Math.max(0, window.scrollY);
      var now = (window.performance && performance.now) ? performance.now() : Date.now();
      if (now < lockedUntil) {
        lastY = y;
        return;
      }
      var dy = y - lastY;
      var shrunk = toolbar.classList.contains('is-shrunk');
      var next = shrunk;
      if (y <= 40) {
        next = false;
      } else if (!shrunk && dy > THRESHOLD) {
        next = true;
      } else if (shrunk && dy < -THRESHOLD) {
        next = false;
      }
      if (next !== shrunk) {
        toolbar.classList.toggle('is-shrunk', next);
        lockedUntil = now + TRANSITION_LOCK_MS;
      }
      lastY = y;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    var onMqlChange = function () {
      if (!mql.matches) toolbar.classList.remove('is-shrunk');
    };
    if (mql.addEventListener) mql.addEventListener('change', onMqlChange);
    else if (mql.addListener) mql.addListener(onMqlChange);
  }


  /* --------------------------------------------------------------------------
     Product Data Extraction
     -------------------------------------------------------------------------- */

  function extractProducts() {
    var originals = document.querySelectorAll('.lf-scroll-col__track > .lf-scroll-col__inner:first-child .lf-card[data-product-id]');
    var products = [];
    var seen = {};

    originals.forEach(function (card) {
      var id = card.getAttribute('data-product-id');
      if (seen[id]) return;
      seen[id] = true;

      var lat = parseFloat(card.getAttribute('data-lat'));
      var lng = parseFloat(card.getAttribute('data-lng'));
      var city = card.getAttribute('data-city');

      if (isNaN(lat) || isNaN(lng)) {
        var coords = lookupCity(city);
        if (coords) { lat = coords.lat; lng = coords.lng; }
        else { lat = null; lng = null; }
      }

      var imgEl = card.querySelector('.lf-card__image');
      var titleEl = card.querySelector('.lf-card__title');
      var priceEl = card.querySelector('.lf-card__price');
      var locationEl = card.querySelector('.lf-card__location');

      // Exotic flag + materials are surfaced by the server-rendered card
      // (theme/snippets/live-feed-product-card.liquid). Read them here once
      // so downstream consumers (duty badges, pin colouring, customs card)
      // don't have to re-query metafields.
      var isExotic = card.getAttribute('data-exotic') === 'true';
      var exoticMaterials = card.getAttribute('data-exotic-materials') || '';

      products.push({
        id: id,
        lat: lat,
        lng: lng,
        url: card.getAttribute('href') || '#',
        image: imgEl ? (imgEl.src || imgEl.currentSrc || '') : '',
        title: titleEl ? titleEl.textContent.trim() : '',
        price: priceEl ? priceEl.textContent.trim() : '',
        locationName: locationEl ? locationEl.textContent.trim() : (city || ''),
        isExotic: isExotic,
        exoticMaterials: exoticMaterials,
        card: card
      });
    });

    return products;
  }

  /* --------------------------------------------------------------------------
     Group Products by Location
     -------------------------------------------------------------------------- */

  function groupByLocation(products) {
    var groups = {};

    products.forEach(function (p) {
      if (p.lat == null || p.lng == null) return;
      var key = Math.round(p.lat * 100) + '_' + Math.round(p.lng * 100);

      if (!groups[key]) {
        groups[key] = {
          key: key,
          lat: p.lat,
          lng: p.lng,
          locationName: p.locationName,
          products: []
        };
      }
      groups[key].products.push(p);
    });

    return groups;
  }

  /* --------------------------------------------------------------------------
     Popup HTML Builder
     -------------------------------------------------------------------------- */

  function buildPopupHTML(group, width) {
    var count = group.products.length;
    var header = '<div class="lf-popup__header">' +
      '<span class="lf-popup__location">' + escapeHTML(group.locationName || 'Unknown') + '</span>' +
      '<span class="lf-popup__count">' + count + (count === 1 ? ' bag' : ' bags') + '</span>' +
      '</div>';

    var cards = group.products.map(function (p) {
      var imgTag = p.image
        ? '<img class="lf-popup__mini-img" src="' + escapeHTML(p.image) + '" alt="" loading="lazy">'
        : '<div class="lf-popup__mini-img"></div>';
      return '<a href="' + escapeHTML(p.url) + '" class="lf-popup__mini" data-product-id="' + escapeHTML(String(p.id)) + '">' +
        imgTag +
        '<span class="lf-popup__mini-title">' + escapeHTML(p.title) + '</span>' +
        '<span class="lf-popup__mini-price">' + escapeHTML(p.price) + '</span>' +
        '</a>';
    }).join('');

    var widthStyle = width ? ' style="width:' + width + 'px"' : '';
    return '<div class="lf-popup"' + widthStyle + '>' + header +
      '<div class="lf-popup__row">' + cards + '</div>' +
      '</div>';
  }

  function escapeHTML(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /* --------------------------------------------------------------------------
     Mobile Bottom Sheet
     -------------------------------------------------------------------------- */

  var bottomSheetEl = null;
  var bottomSheetBackdrop = null;

  function ensureBottomSheet() {
    if (bottomSheetEl) return;

    bottomSheetBackdrop = document.createElement('div');
    bottomSheetBackdrop.className = 'lf-bottom-sheet-backdrop';
    document.body.appendChild(bottomSheetBackdrop);

    bottomSheetEl = document.createElement('div');
    bottomSheetEl.className = 'lf-bottom-sheet';
    bottomSheetEl.innerHTML = '<div class="lf-bottom-sheet__handle" aria-label="Drag down to close" role="button" tabindex="0"></div><div class="lf-bottom-sheet__content"></div>';
    document.body.appendChild(bottomSheetEl);

    bottomSheetBackdrop.addEventListener('click', closeBottomSheet);
    makeDraggableToClose(
      bottomSheetEl,
      bottomSheetEl.querySelector('.lf-bottom-sheet__handle'),
      closeBottomSheet,
      { backdropEl: bottomSheetBackdrop, backdropMode: 'opacity', backdropAlpha: 1 }
    );
  }

  /* Make a panel that already opens by sliding up from the bottom (QV sheet
     or map pin popup on mobile) closeable by dragging its handle downward.
     Uses pointer events so it works for both touch and mouse; the handle's
     `touch-action: none` (set in CSS) keeps the gesture from being eaten
     by the page's vertical scroll on touch devices.

     Release behaviour:
       • dragged > max(80px, 25% of sheet height) → slide the rest of the
         way out and invoke `closeFn`
       • otherwise → snap back to translateY(0)

     If `opts.backdropEl` is provided, its dim is faded linearly with the
     drag distance — full at rest, transparent when fully dragged out. Two
     modes:
       • 'opacity' — for backdrops that are siblings of the sheet (the map
         bottom-sheet's dedicated `.lf-bottom-sheet-backdrop`).
       • 'rgba'    — for backdrops that are the sheet's parent (QV overlay
         contains the modal as a child, so a plain opacity fade would also
         fade the modal). Updates `background-color` instead.

     Inline overrides are cleared on both paths so the next open doesn't
     carry over offset / faded-backdrop state. */
  function makeDraggableToClose(sheetEl, handleEl, closeFn, opts) {
    if (!sheetEl || !handleEl) return;
    opts = opts || {};

    var backdropEl   = opts.backdropEl || null;
    var backdropMode = opts.backdropMode || 'opacity';   // 'opacity' | 'rgba'
    var baseAlpha    = (opts.backdropAlpha != null) ? opts.backdropAlpha : 1;

    var startY = 0;
    var dy = 0;
    var sheetHeight = 0;
    var dragging = false;
    var pid = null;
    var SNAP = 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)';
    var BACKDROP_SNAP = 'background-color 0.22s ease, opacity 0.22s ease';

    function setSheet(t, trans) {
      sheetEl.style.transform = t;
      sheetEl.style.transition = trans;
    }

    function setBackdrop(alpha, trans) {
      if (!backdropEl) return;
      backdropEl.style.transition = trans || '';
      if (backdropMode === 'rgba') {
        backdropEl.style.backgroundColor = 'rgba(0, 0, 0, ' + alpha + ')';
      } else {
        backdropEl.style.opacity = alpha;
      }
    }

    function clearInline() {
      sheetEl.style.transform = '';
      sheetEl.style.transition = '';
      if (backdropEl) {
        backdropEl.style.transition = '';
        if (backdropMode === 'rgba') backdropEl.style.backgroundColor = '';
        else backdropEl.style.opacity = '';
      }
    }

    function onDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      pid = e.pointerId;
      startY = e.clientY;
      dy = 0;
      sheetHeight = sheetEl.getBoundingClientRect().height || 1;
      dragging = true;
      setSheet('translateY(0)', 'none');
      setBackdrop(baseAlpha, 'none');
      try { handleEl.setPointerCapture(pid); } catch (err) {}
      handleEl.addEventListener('pointermove', onMove);
      handleEl.addEventListener('pointerup', onUp);
      handleEl.addEventListener('pointercancel', onUp);
      e.preventDefault();
    }

    function onMove(e) {
      if (!dragging || e.pointerId !== pid) return;
      dy = Math.max(0, e.clientY - startY);
      setSheet('translateY(' + dy + 'px)', 'none');
      var progress = Math.min(1, dy / sheetHeight);
      setBackdrop(baseAlpha * (1 - progress), 'none');
    }

    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      handleEl.removeEventListener('pointermove', onMove);
      handleEl.removeEventListener('pointerup', onUp);
      handleEl.removeEventListener('pointercancel', onUp);
      try { handleEl.releasePointerCapture(pid); } catch (err) {}

      var threshold = Math.max(80, sheetHeight * 0.25);
      if (dy > threshold) {
        setSheet('translateY(100%)', SNAP);
        setBackdrop(0, BACKDROP_SNAP);
        setTimeout(function () {
          closeFn();
          // Clear inline overrides after the close-state is applied so the
          // sheet opens cleanly next time without a stale transform / fade.
          clearInline();
        }, 230);
      } else {
        setSheet('translateY(0)', SNAP);
        setBackdrop(baseAlpha, BACKDROP_SNAP);
        setTimeout(clearInline, 230);
      }
    }

    handleEl.addEventListener('pointerdown', onDown);
  }

  function openBottomSheet(group) {
    ensureBottomSheet();
    var content = bottomSheetEl.querySelector('.lf-bottom-sheet__content');
    content.innerHTML = buildPopupHTML(group);

    bottomSheetBackdrop.classList.add('is-open');
    bottomSheetEl.classList.add('is-open');
  }

  function closeBottomSheet() {
    if (!bottomSheetEl) return;
    bottomSheetEl.classList.remove('is-open');
    bottomSheetBackdrop.classList.remove('is-open');
  }

  function isMobile() {
    return window.innerWidth < 1000;
  }

  /* --------------------------------------------------------------------------
     Tile Pre-warmer
     Silently fetches tiles into the browser cache for each pin location at the
     zoom levels used by flyTo (5-7), so grey squares never appear on hover.
     -------------------------------------------------------------------------- */

  function prewarmTiles(groups) {
    var seen = {};
    var sub  = ['a', 'b', 'c', 'd'];

    function latLngToTile(lat, lng, z) {
      var x = Math.floor((lng + 180) / 360 * (1 << z));
      var r = lat * Math.PI / 180;
      var y = Math.floor((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * (1 << z));
      return { x: x, y: y };
    }

    function load(z, x, y) {
      var k = z + '/' + x + '/' + y;
      if (seen[k]) return;
      seen[k] = true;
      var s = sub[(x + y) % 4];
      (new Image()).src = 'https://' + s + '.basemaps.cartocdn.com/rastertiles/voyager/' + z + '/' + x + '/' + y + '.png';
    }

    // Stagger the requests slightly so they don't compete with initial tile load
    var keys = Object.keys(groups);
    keys.forEach(function (key, i) {
      var g = groups[key];
      setTimeout(function () {
        [5, 6, 7].forEach(function (z) {
          var t = latLngToTile(g.lat, g.lng, z);
          for (var dx = -2; dx <= 2; dx++) {
            for (var dy = -2; dy <= 2; dy++) {
              load(z, t.x + dx, t.y + dy);
            }
          }
        });
      }, 1200 + i * 80); // start after initial tiles settle, 80ms apart per location
    });
  }

  /* --------------------------------------------------------------------------
     Map Module — Leaflet Implementation
     -------------------------------------------------------------------------- */

  function createMap(containerId, products) {
    var container = document.getElementById(containerId);
    if (!container || typeof L === 'undefined') return null;

    var map = L.map(containerId, {
      zoomControl: true,
      scrollWheelZoom: true,
      wheelPxPerZoomLevel: 120,
      zoomSnap: 0.5,
      zoomDelta: 0.5,
      attributionControl: true,
      minZoom: 2,
      // `worldCopyJump` + the tile layer's default horizontal wrap give us
      // infinite east/west panning. This matters since the desktop map
      // element renders 100vw wide behind the list overlay, so only ~45vw
      // is actually visible — the old `maxBounds` clamp stopped center lng
      // at roughly ±60° and made it impossible to pan far enough to see
      // things like Japan or Alaska inside the visible slice. Letting the
      // world wrap sidesteps that entirely, and Leaflet will silently
      // teleport the center back across ±180° so markers stay put.
      worldCopyJump: true,
      // Ease the animation curve — Leaflet's default 0.25 feels abrupt; lower
      // value = gentler deceleration = smoother flyTo finish.
      zoomAnimationThreshold: 6,
      fadeAnimation: true
    }).setView([30, 10], 2);

    // Two-layer basemap so city/country names can be hidden during flight.
    // The base tiles (no labels) are always visible; the label layer is
    // faded to 0 on movestart and back to 1 on moveend, which eliminates
    // the flicker of labels rendering at every intermediate zoom level
    // while Leaflet's flyTo arc zooms out and in.
    var baseTiles = L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png',
      {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 18,
        keepBuffer: 8,
        updateWhenIdle: false,
        crossOrigin: 'anonymous'
      }
    ).addTo(map);

    // Own pane sits above the base tiles but below markers/popups, and has
    // its own z-index so CSS opacity transitions don't fight marker layers.
    map.createPane('labelsPane');
    map.getPane('labelsPane').style.zIndex = 250;
    map.getPane('labelsPane').style.pointerEvents = 'none';

    var labelTiles = L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
      {
        subdomains: 'abcd',
        maxZoom: 18,
        keepBuffer: 4,
        updateWhenIdle: true,
        crossOrigin: 'anonymous',
        pane: 'labelsPane',
        className: 'lf-map-labels'
      }
    ).addTo(map);

    // Track whether this is the very first render vs. a user-initiated move.
    // We only want to fade labels during programmatic flights, not during the
    // initial paint (otherwise labels pop in late on page load).
    var labelsReady = false;
    map.whenReady(function () {
      setTimeout(function () { labelsReady = true; }, 400);
    });

    map.on('movestart zoomstart', function () {
      if (!labelsReady) return;
      var el = labelTiles.getContainer();
      if (el) el.classList.add('is-hidden');
    });

    map.on('moveend zoomend', function () {
      var el = labelTiles.getContainer();
      if (el) el.classList.remove('is-hidden');
    });

    var groups = groupByLocation(products);
    var locationMarkers = {};
    var productToLocation = {};
    var bounds = [];

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      var count = group.products.length;

      var el = document.createElement('div');
      el.className = 'lf-marker';
      if (count > 1) {
        el.setAttribute('data-count', String(count));
      }

      var size = count > 1 ? 24 : 12;
      var icon = L.divIcon({
        className: '',
        html: el.outerHTML,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });

      var marker = L.marker([group.lat, group.lng], { icon: icon }).addTo(map);

      marker.on('click', function () {
        if (isMobile()) {
          openBottomSheet(group);
        } else {
          /* The map element spans 100vw but sits behind the list overlay on
             the left, so only the right ~45vw is actually visible (or the
             full 100vw when the map is expanded). Sizing the popup against
             `map.getSize().x` produced popups wider than the visible slice
             and let them open behind the list. Use the visible slice
             instead, and tell Leaflet's auto-pan how much of the left edge
             is obscured so the popup always lands inside the visible area. */
          var mapContainerEl = map.getContainer();
          var mapContainerRect = mapContainerEl.getBoundingClientRect();
          var mapPanel = document.querySelector('.lf-split__map');
          var splitEl = document.getElementById('lf-split');
          var isExpanded = splitEl && splitEl.classList.contains('lf-split--map-expanded');

          var visibleWidth = mapContainerRect.width;
          var obscuredLeft = 0;
          if (!isExpanded && mapPanel) {
            var panelRect = mapPanel.getBoundingClientRect();
            visibleWidth = panelRect.width;
            obscuredLeft = Math.max(0, panelRect.left - mapContainerRect.left);
          }

          var cardWidth = 140;
          var cardPaddingEachSide = 16; /* 1rem each side */
          var cardGap = 10;
          var count = group.products.length;
          var naturalWidth = cardPaddingEachSide * 2 + count * cardWidth + (count - 1) * cardGap;
          var popupWidth = Math.min(naturalWidth, visibleWidth - 32);
          popupWidth = Math.max(popupWidth, cardWidth + cardPaddingEachSide * 2);

          var popup = L.popup({
            maxWidth: 9999,
            minWidth: 0,
            className: 'lf-map-popup',
            closeButton: false,
            autoPan: true,
            autoPanPaddingTopLeft: L.point(obscuredLeft + 20, 60),
            autoPanPaddingBottomRight: L.point(20, 20)
          })
            .setLatLng([group.lat, group.lng])
            .setContent(buildPopupHTML(group, popupWidth))
            .openOn(map);

          var popupEl = popup.getElement();
          if (popupEl) {
            var row = popupEl.querySelector('.lf-popup__row');
            if (row) {
              row.addEventListener('wheel', function (e) {
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                  e.preventDefault();
                  e.stopPropagation();
                  row.scrollLeft += e.deltaY;
                }
              }, { passive: false });
            }
          }
        }
      });

      locationMarkers[key] = marker;
      bounds.push([group.lat, group.lng]);

      group.products.forEach(function (p) {
        productToLocation[p.id] = key;
      });
    });

    var initialISO = selectedDestISO || detectedISO;
    if (initialISO && COUNTRY_CENTERS[initialISO]) {
      map.setView(COUNTRY_CENTERS[initialISO], countryZoom(), { animate: false });
    } else if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
    }

    prewarmTiles(groups);

    if (bounds.length === 0) {
      var msgEl = document.createElement('div');
      msgEl.className = 'lf-map-message';
      msgEl.textContent = 'Locations appear as bags are sourced worldwide';
      container.style.position = 'relative';
      container.appendChild(msgEl);
    }

    map.on('click', function () {
      closeBottomSheet();
    });

    var TIER_CLASSES = ['lf-marker--none', 'lf-marker--low', 'lf-marker--medium', 'lf-marker--high', 'lf-marker--exotic'];

    function updatePinColors(destISO) {
      Object.keys(locationMarkers).forEach(function (key) {
        var markerEl = locationMarkers[key].getElement();
        if (!markerEl) return;
        var dot = markerEl.querySelector('.lf-marker') || markerEl.firstChild;
        if (!dot) return;
        TIER_CLASSES.forEach(function (c) { dot.classList.remove(c); });
        if (!destISO) return;
        var group = groups[key];
        // Aggregate pin colour only from NON-exotic products at this location.
        // Rationale: exotics can't cross borders, so their "duty tier" isn't
        // meaningful to a buyer deciding on an import destination. If every
        // bag at a pin is exotic (very rare — most pins mix exotic + non-
        // exotic stock) the pin turns black so the buyer immediately sees
        // international isn't an option at that location.
        var nonExotic = group.products.filter(function (p) { return !p.isExotic; });
        if (nonExotic.length === 0) {
          dot.classList.add('lf-marker--exotic');
          return;
        }
        var p = nonExotic[0];
        var bagISO = p.card ? countryRawToISO(p.card.getAttribute('data-country')) : null;
        var result = calcDuty(10000, destISO, bagISO);
        var tier = result ? result.tier : 'none';
        dot.classList.add('lf-marker--' + tier);
      });
    }

    function highlightPin(productId) {
      var locKey = productToLocation[String(productId)];
      Object.keys(locationMarkers).forEach(function (key) {
        var markerEl = locationMarkers[key].getElement();
        if (!markerEl) return;
        var dot = markerEl.querySelector('.lf-marker') || markerEl.firstChild;
        if (!dot) return;
        if (key === locKey) dot.classList.add('is-highlighted');
        else dot.classList.remove('is-highlighted');
      });
    }

    function resetPins() {
      Object.keys(locationMarkers).forEach(function (key) {
        var markerEl = locationMarkers[key].getElement();
        if (!markerEl) return;
        var dot = markerEl.querySelector('.lf-marker') || markerEl.firstChild;
        if (dot) dot.classList.remove('is-highlighted');
      });
    }

    // Longer durations give tiles time to load during the flyTo arc so the
    // map reads as a smooth glide rather than squares popping in. Combined
    // with the label-fade on movestart, cross-continent pans feel clean.
    //
    // "Reset" view semantics: the user's selected/detected country is the
    // anchor the map always returns to — NOT the bounding box of every pin.
    // Fitting bounds across a globally-distributed collection lands the
    // visual centre somewhere over North Africa / the Middle East, which
    // isn't where the user lives and isn't useful context for them.
    // Only fall back to fitBounds if no country has been resolved yet.
    function resetToDefaultView() {
      var iso = selectedDestISO || detectedISO;
      if (iso && COUNTRY_CENTERS[iso]) {
        map.flyTo(COUNTRY_CENTERS[iso], countryZoom(), { duration: 2.0, easeLinearity: 0.25 });
      } else if (bounds.length > 0) {
        map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 6, duration: 2.2, easeLinearity: 0.25 });
      }
    }

    function zoomToPin(productId) {
      var locKey = productToLocation[String(productId)];
      if (locKey && groups[locKey]) {
        map.flyTo([groups[locKey].lat, groups[locKey].lng], 6, { duration: 2.2, easeLinearity: 0.25 });
      }
    }

    function flyToCountry(iso) {
      if (!iso || !COUNTRY_CENTERS[iso]) return;
      map.flyTo(COUNTRY_CENTERS[iso], countryZoom(), { duration: 2.0, easeLinearity: 0.25 });
    }

    setTimeout(function () { map.invalidateSize(); }, 200);

    // Walkthrough helper: find a green (low-duty / none-duty) pin's marker
    // + its DOM element + its group. Returns null if no green pin exists in
    // the current set. Centralised so the walkthrough doesn't have to
    // re-implement the lookup or rely on DOM-dispatched clicks (which iOS
    // Safari sometimes drops before they reach Leaflet's event wrapper).
    function findGreenPin() {
      var keys = Object.keys(locationMarkers);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        var marker = locationMarkers[key];
        var el = marker.getElement();
        if (!el) continue;
        var dot = el.querySelector('.lf-marker') || el.firstChild;
        if (!dot) continue;
        if (dot.classList.contains('lf-marker--low') || dot.classList.contains('lf-marker--none')) {
          return { marker: marker, el: el, group: groups[key], lat: groups[key].lat, lng: groups[key].lng };
        }
      }
      return null;
    }

    // Fires the marker's click via Leaflet's own event system. Bypasses the
    // DOM synthetic-click path that some mobile browsers don't propagate
    // into Leaflet's listeners, so the popup / bottom sheet reliably opens.
    function triggerSamplePin(pin) {
      if (!pin) pin = findGreenPin();
      if (!pin) return null;
      pin.marker.fire('click');
      return pin;
    }

    return {
      highlightPin: highlightPin,
      resetPins: resetPins,
      resetView: resetToDefaultView,
      zoomToPin: zoomToPin,
      flyToCountry: flyToCountry,
      updatePinColors: updatePinColors,
      findGreenPin: findGreenPin,
      triggerSamplePin: triggerSamplePin,
      leafletMap: map
    };
  }

  /* --------------------------------------------------------------------------
     Card-Pin Linking
     -------------------------------------------------------------------------- */

  function scrollToCard(card) {
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.classList.remove('is-pin-target');
    void card.offsetWidth;
    card.classList.add('is-pin-target');
    setTimeout(function () { card.classList.remove('is-pin-target'); }, 2200);
  }

  function initCardHoverLinking(products, mapInstance) {
    if (!mapInstance) return;

    var resetTimer = null;

    products.forEach(function (p) {
      var allCards = document.querySelectorAll('.lf-card[data-product-id="' + p.id + '"]');
      allCards.forEach(function (card) {
        card.addEventListener('mouseenter', function () {
          clearTimeout(resetTimer);
          mapInstance.highlightPin(p.id);
          mapInstance.zoomToPin(p.id);
          allCards.forEach(function (c) { c.classList.add('is-highlighted'); });
        });

        card.addEventListener('mouseleave', function () {
          mapInstance.resetPins();
          allCards.forEach(function (c) { c.classList.remove('is-highlighted'); });
          resetTimer = setTimeout(function () {
            mapInstance.resetView();
          }, 250);
        });
      });
    });
  }

  /* --------------------------------------------------------------------------
     Shared Hot-Bag PDP + Quick-View helpers

     The quick-view modal and the dedicated Hot Bags PDP (product.hotbag.json
     → main-hotbag-product.liquid) both need the same two behaviours:

       1. An Estimated Import Costs card with a country picker that
          re-renders a live duty breakdown whenever the destination changes.
       2. A cart form whose hidden line-item properties stay in sync with
          the selected shipping method + (for International shipping)
          estimated import duties, so orders from either surface carry
          identical metadata into the admin / invoice.

     The helpers below take a `root` DOM element and a `ds` (data source)
     object — they're agnostic to whether the root is the modal's content
     element or the PDP section. Both return a renderer function that takes
     a destination ISO code; the caller assigns those renderers to the
     module-level `qvCustomsRenderer` / `qvCartPropsRenderer` slots so
     `onDestChange` can re-run them as the user changes country.
     -------------------------------------------------------------------------- */

  /**
   * Mount the Estimated Import Costs card into `root`.
   *
   *   root       — container to search for a `.lf-qv__actions` / customs-slot
   *                anchor. The card is inserted immediately before `beforeEl`
   *                (or before the first `.lf-qv__actions` if `beforeEl` is
   *                falsy) so the DOM order stays customs → actions.
   *   beforeEl   — element to insert the card before (can be null)
   *   ds         — { price: number (minor units), bagISO: string }
   *
   * Always mounts (so the destination picker is visible from page load) as
   * long as `root` is provided. The render function handles missing/invalid
   * data (null destISO, missing bagISO, NaN price) with a placeholder.
   *
   * Returns `{ el, render(destISO), destroy() }`.
   */
  function mountCustomsCard(root, beforeEl, ds) {
    if (!root) return null;
    ds = ds || {};

    var hasCalcInputs = Boolean(ds.bagISO) && !isNaN(ds.price);
    var isExotic = ds.isExotic === true;

    var customsEl = document.createElement('div');
    customsEl.className = 'lf-qv__customs';
    if (isExotic) customsEl.setAttribute('data-lf-exotic', 'true');

    var hdrEl = document.createElement('div');
    hdrEl.className = 'lf-qv__customs-header';
    var titleEl = document.createElement('span');
    titleEl.className = 'lf-qv__customs-title';
    titleEl.textContent = 'Estimated Import Costs';
    hdrEl.appendChild(titleEl);

    // Country picker is suppressed for exotic bags — they don't have a
    // destination to calculate duty against, so there's nothing to pick.
    var picker = null;
    if (!isExotic) {
      picker = createCountryPicker({
        showTiers: hasCalcInputs,
        priceMinorUnits: hasCalcInputs ? ds.price : 0,
        bagISO: ds.bagISO || null,
        fixed: true,
        onChange: onDestChange
      });
      hdrEl.appendChild(picker.el);
    }
    customsEl.appendChild(hdrEl);

    var bodyEl = document.createElement('div');
    bodyEl.className = 'lf-qv__customs-body';
    customsEl.appendChild(bodyEl);

    var anchor = beforeEl || root.querySelector('.lf-qv__actions');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(customsEl, anchor);
    } else {
      root.appendChild(customsEl);
    }

    function render(destISO) {
      if (picker) picker.sync(destISO);

      if (isExotic) {
        var materials = (ds.exoticMaterials || '').trim();
        var label = materials ? materials.toLowerCase() : 'exotic';
        bodyEl.innerHTML =
          '<p class="lf-qv__customs-empty lf-qv__customs-empty--exotic">' +
          'International shipping not available for ' + escapeHTML(label) + ' bags.' +
          '</p>';
        return;
      }
      if (!destISO) {
        bodyEl.innerHTML = '<p class="lf-qv__customs-empty">Select a destination country above to see estimated import duties for this bag.</p>';
        return;
      }
      if (!hasCalcInputs) {
        bodyEl.innerHTML = '<p class="lf-qv__customs-empty">Import duty estimate isn\'t available for this bag — contact us for a quote.</p>';
        return;
      }
      var result = calcDuty(ds.price, destISO, ds.bagISO);
      bodyEl.innerHTML = buildCustomsBodyHTML(result);
    }

    return {
      el: customsEl,
      picker: picker,
      render: render,
      destroy: function () { if (picker) picker.destroy(); }
    };
  }

  /**
   * Wire hidden `properties[…]` inputs on the cart-add form inside `root`
   * to the currently-selected shipping method + duty estimate.
   *
   *   root — container with a `[data-lf-cart-form]` form inside it
   *   ds   — { price, bagISO, bagCity }
   *
   * Returns `render(destISO)` or null if no form is found.
   */
  function wireHotbagCartForm(root, ds) {
    if (!root || !ds) return null;
    var cartForm = root.querySelector('[data-lf-cart-form]');
    if (!cartForm) return null;

    // Read the SSR-baked private mirror of the remaining balance so each
    // render can recompute the customer-visible "Balance due to Lilac Blue"
    // (= remaining + selected shipping cost) without re-walking the DOM.
    var remainingInput = cartForm.querySelector('input[name="properties[_remaining_minor]"]');
    var remainingMinor = remainingInput ? (parseInt(remainingInput.value, 10) || 0) : 0;
    var hasDeposit = remainingMinor > 0;

    return function render(destISO) {
      var radio = root.querySelector('.lf-qv__shipping-radio:checked');
      // Default fallback: exotics can't ship internationally, so fall back
      // to local delivery if no radio has wired yet (pre-init window).
      var fallbackMethod = ds.isExotic ? 'local' : 'international';
      var method = radio ? radio.getAttribute('data-lf-shipping-method') : fallbackMethod;

      var shipMinor = shippingCostMinor(method);
      var shipCostLabel = shipMinor === 0 ? 'Free' : fmt(shipMinor);
      setFormProp(cartForm, 'Shipping', shippingMethodLabel(method, ds.bagCity) + ' · ' + shipCostLabel);
      setFormProp(cartForm, '_shipping_method_code', method);
      setFormProp(cartForm, '_shipping_cost_minor',  String(shipMinor));
      // Clean up the previous-generation property name in case a stale
      // form (cached HTML, in-progress page) still carries it.
      setFormProp(cartForm, 'Shipping method', null);

      // Balance due to Lilac Blue = remaining balance + shipping. Duties
      // are charged by customs (not by us) so they are explicitly NOT
      // rolled into this total — that's the line the buyer takes to
      // their bank.
      if (hasDeposit) {
        var balanceDueMinor = remainingMinor + shipMinor;
        setFormProp(cartForm, 'Balance due to Lilac Blue', fmt(balanceDueMinor));
        setFormProp(cartForm, '_balance_due_minor',         String(balanceDueMinor));
      }

      // Duty props are only meaningful for International Shipping. Exotic
      // bags never reach this branch (International radio is suppressed),
      // but we defensively also check ds.isExotic so duty props can never
      // leak onto an exotic order even if someone resurrects the radio.
      var showDuties = method === 'international' && !ds.isExotic && destISO && ds.bagISO && !isNaN(ds.price);
      var result = showDuties ? calcDuty(ds.price, destISO, ds.bagISO) : null;

      if (result) {
        var destName   = ISO_NAMES[destISO] || destISO;
        var dutyMinor  = Math.round(result.dutyAmt * 100);
        var vatMinor   = Math.round(result.vatAmt  * 100);
        var totalMinor = Math.round(result.total   * 100);
        // Visual divider before the customs block so it reads as a
        // separate section ("not paid to us"). Distinct property name
        // from the SSR'd divider above so the two coexist. Rides
        // alongside duties so it disappears too when duties don't apply.
        setFormProp(cartForm, '——', '—————');
        setFormProp(cartForm, 'Estimated import duties', fmt(totalMinor) + ' to ' + destName);
        // Disclaimer rides alongside the duties row so the buyer always
        // sees the framing "this is an estimate, paid to customs not us".
        setFormProp(cartForm, 'About import duties',
          'Estimate only. Charged by customs in your destination on arrival and payable directly to them — not collected by Lilac Blue.');
        setFormProp(cartForm, '_duty_minor',          String(dutyMinor));
        setFormProp(cartForm, '_vat_minor',           String(vatMinor));
        setFormProp(cartForm, '_duty_rate',           String(result.dutyRate));
        setFormProp(cartForm, '_vat_rate',            String(result.vatRate));
        setFormProp(cartForm, '_total_customs_minor', String(totalMinor));
        setFormProp(cartForm, '_duty_dest_iso',       destISO);
      } else {
        setFormProp(cartForm, '——',                      null);
        setFormProp(cartForm, 'Estimated import duties', null);
        setFormProp(cartForm, 'About import duties',     null);
        setFormProp(cartForm, '_duty_minor',             null);
        setFormProp(cartForm, '_vat_minor',              null);
        setFormProp(cartForm, '_duty_rate',              null);
        setFormProp(cartForm, '_vat_rate',               null);
        setFormProp(cartForm, '_total_customs_minor',    null);
        setFormProp(cartForm, '_duty_dest_iso',          null);
      }
    };
  }

  /* --------------------------------------------------------------------------
     Quick View Modal
     -------------------------------------------------------------------------- */

  var qvOverlay = null;
  var qvContentEl = null;
  var qvFooterEl = null;

  function ensureQvModal() {
    if (qvOverlay) return;

    qvOverlay = document.createElement('div');
    qvOverlay.className = 'lf-qv-overlay';
    qvOverlay.id = 'lf-qv-overlay';
    qvOverlay.setAttribute('role', 'dialog');
    qvOverlay.setAttribute('aria-modal', 'true');
    qvOverlay.innerHTML =
      '<div class="lf-qv-modal">' +
        '<div class="lf-qv-modal__handle" aria-label="Drag down to close" role="button" tabindex="0"></div>' +
        '<button class="lf-qv-modal__close" aria-label="Close quick view">' +
          '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 1l10 10M11 1L1 11"/></svg>' +
        '</button>' +
        '<div class="lf-qv-modal__content"></div>' +
        '<div class="lf-qv-modal__footer" hidden></div>' +
      '</div>';

    document.body.appendChild(qvOverlay);
    qvContentEl = qvOverlay.querySelector('.lf-qv-modal__content');
    qvFooterEl = qvOverlay.querySelector('.lf-qv-modal__footer');

    qvOverlay.querySelector('.lf-qv-modal__close').addEventListener('click', closeQv);
    qvOverlay.addEventListener('click', function (e) {
      if (e.target === qvOverlay) closeQv();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeQv();
    });

    // Handle is display:none on desktop (no swipe affordance); on mobile
    // the QV opens as a bottom sheet and the handle becomes draggable.
    // Backdrop alpha matches the CSS at-rest dim of rgba(0,0,0,0.45).
    makeDraggableToClose(
      qvOverlay.querySelector('.lf-qv-modal'),
      qvOverlay.querySelector('.lf-qv-modal__handle'),
      closeQv,
      { backdropEl: qvOverlay, backdropMode: 'rgba', backdropAlpha: 0.45 }
    );
  }

  /**
   * Wire the shipping-card radios to the customs card visibility.
   *
   * The shipping card (rendered server-side via `live-feed-shipping-card.liquid`)
   * lets the visitor pick one of up to four shipping methods. Methods 1–3 are
   * domestic (collection, white-glove, local delivery) — import duties are
   * irrelevant, so we hide the adjacent `.lf-qv__customs` card. Method 4
   * (International) shows the customs card again.
   *
   * The customs card is only present when `selectedDestISO` was set at the
   * time the quick view opened (see openQv). If it isn't injected, this is
   * a no-op on toggle — selecting methods just stays cosmetic.
   */
  function initQvShipping(root) {
    var shippingEl = root.querySelector('[data-lf-shipping]');
    if (!shippingEl) return;

    var radios = shippingEl.querySelectorAll('.lf-qv__shipping-radio');
    if (!radios.length) return;

    function applyState(method) {
      var customsEl = root.querySelector('.lf-qv__customs');
      if (!customsEl) return;
      customsEl.hidden = method !== 'international';
    }

    radios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        applyState(r.getAttribute('data-lf-shipping-method'));
        // Refresh `Shipping method` + duty properties on the cart form so
        // the selected method (and whether duties apply) is reflected in
        // the line item properties at checkout time.
        if (qvCartPropsRenderer) qvCartPropsRenderer(selectedDestISO);
      });
    });

    var initiallyChecked = shippingEl.querySelector('.lf-qv__shipping-radio:checked');
    if (initiallyChecked) {
      applyState(initiallyChecked.getAttribute('data-lf-shipping-method'));
    }
  }

  function openQv(card) {
    var tmpl = card.querySelector('template.lf-qv-data');
    if (!tmpl) return;

    ensureQvModal();
    qvContentEl.innerHTML = '';
    qvFooterEl.innerHTML = '';
    qvFooterEl.hidden = true;
    qvContentEl.scrollTop = 0;
    qvContentEl.appendChild(tmpl.content.cloneNode(true));

    var ds = {
      price:    parseInt(card.getAttribute('data-price'), 10),
      bagISO:   countryRawToISO(card.getAttribute('data-country')),
      bagCity:  card.getAttribute('data-city') || '',
      isExotic: card.getAttribute('data-exotic') === 'true',
      exoticMaterials: card.getAttribute('data-exotic-materials') || ''
    };

    // Always mount the customs card for non-exotic bags so the country picker
    // shows even before a destination has been resolved. For exotic bags the
    // card renders as an informational note (no picker) and must always mount
    // so the customer sees why international shipping isn't offered.
    if (selectedDestISO || ds.isExotic) {
      var actions = qvContentEl.querySelector('.lf-qv__actions');
      var mount = mountCustomsCard(qvContentEl, actions, ds);
      if (mount) {
        currentQvPicker = mount.picker;
        qvCustomsRenderer = mount.render;
        qvCustomsRenderer(selectedDestISO);
      }
    }

    // Cart-form line-item properties. Bake the selected shipping method
    // and (for International) the estimated import duties into hidden
    // `properties[…]` inputs on the /cart/add form so they flow through
    // to the order and invoice. Re-runs on radio change (initQvShipping)
    // and on destination-country change (onDestChange → qvCartPropsRenderer).
    var cartRenderer = wireHotbagCartForm(qvContentEl, ds);
    if (cartRenderer) {
      qvCartPropsRenderer = cartRenderer;
      qvCartPropsRenderer(selectedDestISO);
    }

    // Shipping card lives in the template (server-rendered). It controls
    // the customs card's visibility: selecting a domestic method (1–3)
    // hides customs; picking International (4, default) shows it. Must
    // run AFTER customs is inserted so the listener can find it on init.
    initQvShipping(qvContentEl);

    // Relocate the Add to Bag / Full Details stack into the pinned modal
    // footer so the buy CTAs are immediately visible when the overlay
    // opens — no scrolling required on long cards (mobile or desktop
    // with specs + customs). Happens AFTER customs is inserted so the
    // customs block keeps its position inside the detail column.
    var actionsNode = qvContentEl.querySelector('.lf-qv__actions');
    if (actionsNode) {
      qvFooterEl.appendChild(actionsNode);
      qvFooterEl.hidden = false;
    }

    qvOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeQv() {
    if (!qvOverlay) return;
    qvOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    qvCustomsRenderer = null;
    qvCartPropsRenderer = null;
    if (currentQvPicker) { currentQvPicker.destroy(); currentQvPicker = null; }
  }

  function initQuickView() {
    document.addEventListener('click', function (e) {
      // Opt-out regions (e.g. the AI image disclaimer expander) — let their
      // own handlers run without hijacking the click into a quick-view open
      // or letting the underlying card anchor navigate.
      if (e.target.closest('[data-no-qv]')) {
        e.preventDefault();
        return;
      }

      var card = e.target.closest('.lf-card');
      if (card) {
        e.preventDefault();
        openQv(card);
        return;
      }

      var miniCard = e.target.closest('.lf-popup__mini');
      if (miniCard) {
        e.preventDefault();
        var productId = miniCard.getAttribute('data-product-id');
        var originalCard = document.querySelector(
          '.lf-scroll-col__track > .lf-scroll-col__inner:first-child .lf-card[data-product-id="' + productId + '"]'
        );
        if (originalCard) openQv(originalCard);
        return;
      }
    });
  }

  /* --------------------------------------------------------------------------
     AI image disclaimer — expand / collapse
     Toggles the long-form explanation beneath the short caption on both the
     product card AND the quick-view modal. Works on any ancestor carrying
     `.lf-card__ai-disclaimer` or `.lf-qv__ai-disclaimer`.
     -------------------------------------------------------------------------- */

  function initAiDisclaimer() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-ai-toggle]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      var wrap = btn.closest('.lf-card__ai-disclaimer, .lf-qv__ai-disclaimer');
      if (!wrap) return;

      var expanded = wrap.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      var longEl = wrap.querySelector('.lf-card__ai-disclaimer-long, .lf-qv__ai-disclaimer-long');
      if (longEl) longEl.hidden = !expanded;
    });
  }


  /* --------------------------------------------------------------------------
     Mobile Toggle
     -------------------------------------------------------------------------- */

  function initMobileToggle(mapInstance) {
    var toggle = document.getElementById('lf-mobile-toggle');
    var split = document.getElementById('lf-split');
    if (!toggle || !split) return;

    var mapActive = false;
    var firstActivation = true;

    // Leaflet needs the container to have real dimensions at init time. On
    // mobile the map panel is `display: none` by default, so the first time
    // we show it Leaflet has only ever seen a 0×0 container and no tiles
    // will have been requested. After unhiding we call invalidateSize
    // across multiple frames so the tile grid is recomputed against the
    // now-real viewport. The stored centre + zoom (country centre or world
    // view) survive the 0×0 init, so no extra setView is needed.
    function wakeUpMap() {
      var map = mapInstance && mapInstance.leafletMap;
      if (!map) return;

      var invalidate = function () { map.invalidateSize(false); };

      requestAnimationFrame(function () {
        invalidate();
        requestAnimationFrame(invalidate);
      });
      setTimeout(invalidate, 120);
      setTimeout(invalidate, 320);
      if (firstActivation) {
        firstActivation = false;
        setTimeout(invalidate, 600);
      }
    }

    toggle.addEventListener('click', function () {
      mapActive = !mapActive;
      if (mapActive) {
        split.classList.add('lf-split--map-active');
        toggle.innerHTML = '<svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg> Show List';
        wakeUpMap();
      } else {
        split.classList.remove('lf-split--map-active');
        toggle.innerHTML = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M12 2a10 10 0 0 0-7 17l7 3 7-3A10 10 0 0 0 12 2z"/></svg> Show Map';
      }
    });

    // Viewport changes (rotation, address-bar collapse) after the map is
    // visible would otherwise leave gray tiles until the next interaction.
    window.addEventListener('resize', function () {
      if (mapActive) wakeUpMap();
    });
    window.addEventListener('orientationchange', function () {
      if (mapActive) wakeUpMap();
    });
  }

  /* --------------------------------------------------------------------------
     Column Clip — cap scroll so the user can't scroll past the point where both
     columns still fill the viewport.

     The seamless auto-scroll relies on each column's track being 2x the content.
     Without clipping, the column contributes its full 2x height to page flow,
     which lets the user scroll deep into duplicated/empty space. By forcing
     every .lf-scroll-col to the SHORTER column's 1x content height (via an
     --lf-col-clip CSS variable + overflow:hidden), page scroll naturally stops
     at the last row where both columns are still filled with cards.
     -------------------------------------------------------------------------- */

  function initColumnClip() {
    var cols = document.querySelectorAll('.lf-scroll-col');
    if (cols.length === 0) return;

    var root = document.documentElement;

    // Mobile (<700px) renders a flat single-column list via `.lf-columns-mobile`
    // and the animated two-column block is hidden. There's nothing to clip —
    // and measuring a display:none column returns 0, which would wrongly
    // pin --lf-col-clip to 0 and zero-height the page. Skip entirely.
    var MOBILE_BREAKPOINT = 699;
    function isMobile() { return window.innerWidth <= MOBILE_BREAKPOINT; }

    function measure() {
      if (isMobile()) {
        root.style.removeProperty('--lf-col-clip');
        return;
      }
      var heights = [];
      cols.forEach(function (col) {
        var firstInner = col.querySelector('.lf-scroll-col__track > .lf-scroll-col__inner');
        if (firstInner) {
          var h = firstInner.offsetHeight;
          if (h > 0) heights.push(h);
        }
      });
      if (heights.length === 0) return;
      var shorter = Math.min.apply(null, heights);
      root.style.setProperty('--lf-col-clip', shorter + 'px');
    }

    measure();

    if (document.readyState !== 'complete') {
      window.addEventListener('load', measure, { once: true });
    }

    var imgs = document.querySelectorAll(
      '.lf-scroll-col__track > .lf-scroll-col__inner:first-child img'
    );
    imgs.forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', measure, { once: true });
      img.addEventListener('error', measure, { once: true });
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 120);
    });
  }

  /* --------------------------------------------------------------------------
     Init
     -------------------------------------------------------------------------- */

  /* --------------------------------------------------------------------------
     Deep-link quick view from ?qv=<product-id>

     Homepage Trading Floor mini cards route users here with ?qv=<id> so the
     landing experience is the collection page with that bag already opened.
     We strip the param from the URL after opening so a refresh doesn't
     re-trigger unexpectedly.
     -------------------------------------------------------------------------- */

  function initQvDeepLink() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var qvId = params.get('qv');
    if (!qvId) return;

    var card = document.querySelector(
      '.lf-scroll-col__track > .lf-scroll-col__inner:first-child .lf-card[data-product-id="' + qvId + '"]'
    );
    if (!card) return;

    openQv(card);

    try {
      params.delete('qv');
      var cleanSearch = params.toString();
      var newUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    } catch (e) { /* noop */ }
  }

  /* --------------------------------------------------------------------------
     "What is the Trading Floor?" explainer overlay
     Lightweight open/close controller — opens on [data-explain-open], closes
     on [data-explain-close], a click on the backdrop, or the Escape key.
     Body scroll is locked while open.
     -------------------------------------------------------------------------- */

  function initExplainOverlay() {
    var overlay = document.getElementById('lf-explain-overlay');
    if (!overlay) return;

    var openers = document.querySelectorAll('[data-explain-open]');
    if (!openers.length) return;

    var modal      = overlay.querySelector('.lf-explain-modal');
    var lastOpener = null;

    function open(ev) {
      if (ev) ev.preventDefault();
      lastOpener = ev && ev.currentTarget ? ev.currentTarget : null;
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      // Focus the close button so Escape / Enter work immediately for
      // keyboard users.
      var closeBtn = overlay.querySelector('[data-explain-close]');
      if (closeBtn) { setTimeout(function () { closeBtn.focus(); }, 30); }
    }

    function close() {
      if (overlay.hidden) return;
      overlay.hidden = true;
      document.body.style.overflow = '';
      if (lastOpener && typeof lastOpener.focus === 'function') lastOpener.focus();
    }

    openers.forEach(function (el) { el.addEventListener('click', open); });

    overlay.addEventListener('click', function (e) {
      // Click on backdrop (the overlay itself, not the inner modal) closes.
      if (e.target === overlay) close();
      // Explicit close buttons inside the modal.
      if (e.target.closest && e.target.closest('[data-explain-close]')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hidden) close();
    });
  }

  /* --------------------------------------------------------------------------
     Desktop Map-Expand Toggle
     On viewports ≥ 1000px, a round chevron button pinned to the map's
     left edge swaps between [list | right-45% map] and [map full-bleed].
     The expand animation is purely compositor-driven:

       • List panel: `transform: translateX(-100%)` — slides off the left.
       • Map element (#lf-map): CSS `left` shifts from −27.5vw → −55vw
         (relative to the 45vw map panel). The map element has been at
         the final 100vw width since init, so Leaflet never sees a size
         change — no tile re-stream, no grey flash. The country that sat
         at page 77.5vw (centre of the visible right-45% slice) smoothly
         slides to page 50vw (centre of the full viewport) over the same
         duration as the list slide-off, so the reveal reads as one
         coordinated motion.

     Chevron flips ← → → on toggle. Hidden on mobile (list/map sheet
     toggle owns that surface there).
     -------------------------------------------------------------------------- */

  function initMapExpandToggle(mapInstance) {
    var split = document.getElementById('lf-split');
    var mapPanel = document.getElementById('lf-map-container');
    if (!split || !mapPanel || !mapInstance) return;

    var chevronLeft  = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3L5 8l5 5"/></svg>';
    var chevronRight = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3l5 5-5 5"/></svg>';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lf-map-expand-btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Expand map');
    btn.title = 'Expand map';
    btn.innerHTML = chevronLeft;
    mapPanel.appendChild(btn);

    var expanded = false;

    function setExpanded(next) {
      if (next === expanded) return;
      expanded = next;
      split.classList.toggle('lf-split--map-expanded', expanded);
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      btn.setAttribute('aria-label', expanded ? 'Collapse map' : 'Expand map');
      btn.title = expanded ? 'Collapse map' : 'Expand map';
      btn.innerHTML = expanded ? chevronRight : chevronLeft;
    }

    btn.addEventListener('click', function () { setExpanded(!expanded); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && expanded) setExpanded(false);
    });
  }

  /* --------------------------------------------------------------------------
     Hot Bags PDP initialiser

     Runs on the dedicated Trading Floor product page (template:
     `product.hotbag.json` → `main-hotbag-product.liquid`). Mounts the same
     customs / cart-form / shipping wiring used inside the quick-view modal
     so both surfaces produce identical order metadata.

     Independent of the Trading Floor collection page:

       • No `#lf-split` → `initCustomsDuty` bails, so this function handles
         its own country detection and seeds `selectedDestISO` directly.
       • Writes renderers into the same module-level slots (`qvCustomsRenderer`
         / `qvCartPropsRenderer`) that `onDestChange` reads, so changing
         country via the PDP's picker re-renders its own card.
     -------------------------------------------------------------------------- */

  function initHotbagPdp() {
    var root = document.querySelector('[data-hotbag-pdp]');
    if (!root) return;

    // moneyFormat is used by fmt() in the customs body + cart-form duty
    // line. initCustomsDuty seeds it from #lf-split on the collection page;
    // on the PDP we seed from the section's own data attribute instead.
    var mf = root.getAttribute('data-money-format');
    if (mf) moneyFormat = mf;

    var ds = {
      price:    parseInt(root.getAttribute('data-price'), 10),
      bagISO:   countryRawToISO(root.getAttribute('data-country')),
      bagCity:  root.getAttribute('data-city') || '',
      isExotic: root.getAttribute('data-exotic') === 'true',
      exoticMaterials: root.getAttribute('data-exotic-materials') || ''
    };

    var customsSlot = root.querySelector('[data-lf-customs-slot]');

    // Mount customs card + wire cart form props immediately so the country
    // picker is available from page load, independent of the async
    // geolocation lookup below. render(null) shows a picker + placeholder
    // body; subsequent re-renders (onDestChange, detectUserCountry
    // callback) fill in the duty estimate.
    var mount = mountCustomsCard(root, customsSlot, ds);
    if (mount) {
      currentQvPicker = mount.picker;
      qvCustomsRenderer = mount.render;
    }

    var cartRenderer = wireHotbagCartForm(root, ds);
    if (cartRenderer) qvCartPropsRenderer = cartRenderer;

    // Shipping radios toggle customs visibility and keep the cart form
    // shipping-method property in sync — same helper the QV modal uses.
    initQvShipping(root);

    if (qvCustomsRenderer) qvCustomsRenderer(selectedDestISO);
    if (qvCartPropsRenderer) qvCartPropsRenderer(selectedDestISO);

    // If no destination is known yet, geolocate in the background. The
    // picker's own onDestChange handler covers manual picks.
    if (!selectedDestISO) {
      detectUserCountry(root, function (iso) {
        detectedISO = iso;
        var saved = null;
        try { saved = localStorage.getItem('lf-dest-iso'); } catch (e) {}
        selectedDestISO = (saved && ISO_NAMES[saved]) ? saved : iso;
        if (qvCustomsRenderer) qvCustomsRenderer(selectedDestISO);
        if (qvCartPropsRenderer) qvCartPropsRenderer(selectedDestISO);
      });
    }
  }

  /* --------------------------------------------------------------------------
     Filters Panel toggle
     Slides .lf-filters in from the left and (on desktop) pushes the list
     right by 45vw via .lf-split--filters-open. Native form GET submit on
     Apply does the actual filtering — no AJAX. Mutually exclusive with
     map-expand: opening filters collapses the map first so the two transforms
     don't fight.
     -------------------------------------------------------------------------- */

  function initFiltersPanel() {
    var split    = document.getElementById('lf-split');
    var panel    = document.getElementById('lf-filters');
    var backdrop = document.getElementById('lf-filters-backdrop');
    var openers  = document.querySelectorAll('[data-lf-filters-open]');
    var closers  = document.querySelectorAll('[data-lf-filters-close]');
    if (!split || !panel || !openers.length) return;

    var mobileMq = window.matchMedia('(max-width: 999px)');
    var lastOpener = null;
    var open = false;

    function setOpen(next) {
      if (next === open) return;
      open = next;

      if (open && split.classList.contains('lf-split--map-expanded')) {
        split.classList.remove('lf-split--map-expanded');
      }

      split.classList.toggle('lf-split--filters-open', open);
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');

      openers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      if (backdrop) {
        if (open) backdrop.removeAttribute('hidden');
        else backdrop.setAttribute('hidden', '');
      }

      if (mobileMq.matches) {
        document.body.style.overflow = open ? 'hidden' : '';
      } else {
        document.body.style.overflow = '';
      }

      if (open) {
        var closeBtn = panel.querySelector('[data-lf-filters-close]');
        if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 30);
      } else if (lastOpener && typeof lastOpener.focus === 'function') {
        lastOpener.focus();
      }
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        lastOpener = e.currentTarget;
        setOpen(!open);
      });
    });

    closers.forEach(function (el) {
      el.addEventListener('click', function () { setOpen(false); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && open) setOpen(false);
    });

    mobileMq.addEventListener('change', function () {
      if (!open) return;
      document.body.style.overflow = mobileMq.matches ? 'hidden' : '';
    });
  }

  /* --------------------------------------------------------------------------
     Trading Floor Walkthrough
     4-step guided overlay that auto-opens on first visit (gated by the
     `lf_walkthrough_seen` cookie) and is re-openable from the "What is the
     Trading Floor?" modal via [data-wt-launch]. A radial ring uses a giant
     `box-shadow` to dim the page around the target while keeping the target
     itself visible. Slide copy lives here so all walkthrough state is in
     one place.
     -------------------------------------------------------------------------- */

  var WT_COOKIE = 'lf_walkthrough_seen';

  // ─── Scripted slides ──────────────────────────────────────────────────────
  // Each slide drives a short scene that simulates the interaction the buyer
  // will perform themselves: open the destination picker → land on detected;
  // expand the map → ring a green pin → open its popup; click a card → ring
  // the price comparison; ring the Reserve-with-Deposit button.
  //
  // The `script(api)` function is invoked when the slide becomes active. It
  // receives an api with:
  //   - api.pointAt(target, opts)  — animate the ring onto a target element
  //   - api.delay(ms)              — Promise that resolves only if the slide
  //                                  is still the active one (so the chain
  //                                  short-circuits when the user navigates
  //                                  away mid-animation)
  //   - api.onLeaveSlide(fn)       — runs when this specific slide is left
  //   - api.onClose(fn)            — runs when the walkthrough fully closes
  //
  // Defaults: title, subtitle, body strings; `padding`, `shape` for the
  // initial ring placement before the script takes over.
  function getMapInstance() { return mapInstance; }

  function findGreenMarkerEl() {
    // Prefer 'low' (lowest non-zero duty) for a meaningful demo; fall back
    // to 'none' (zero-duty corridors) which also show as green.
    var dot = document.querySelector('.leaflet-marker-icon .lf-marker--low')
           || document.querySelector('.leaflet-marker-icon .lf-marker--none');
    if (!dot) {
      dot = document.querySelector('.lf-marker--low') || document.querySelector('.lf-marker--none');
    }
    return dot ? dot.closest('.leaflet-marker-icon') : null;
  }

  function findFirstCardWithMarketPrice() {
    var row = document.querySelector('.lf-card .lf-card__market-price');
    return row ? row.closest('.lf-card') : document.querySelector('.lf-card');
  }

  // ─── State reconciler ─────────────────────────────────────────────────────
  // Each slide declares a `requires` object — the big-picture UI state it
  // needs to start (map expanded, QV open, etc.). On every slide change we
  // diff against the current state and only flip what needs flipping. That
  // means 3 ↔ 4 (both need QV) doesn't re-render the modal, but 3 → 2
  // closes it cleanly. Walkthrough close reconciles to {} — everything off.
  function getCurrentWtState() {
    var split = document.getElementById('lf-split');
    return {
      mapExpanded: !!(split && split.classList.contains('lf-split--map-expanded')),
      mapVisible:  !!(split && split.classList.contains('lf-split--map-active')),
      pickerOpen:  !!document.querySelector('.lf-toolbar .lf-cpick.is-open'),
      qv:          !!document.querySelector('.lf-qv-overlay.is-open'),
      mapPopup:    !!(document.querySelector('.lf-map-popup') || document.querySelector('.lf-bottom-sheet.is-open'))
    };
  }

  function reconcileWtState(req) {
    req = req || {};
    var cur = getCurrentWtState();
    var mq  = window.matchMedia('(max-width: 999px)');
    var split = document.getElementById('lf-split');

    // Close-first ordering: clear anything we don't need before opening new
    // things. Avoids a brief moment where the QV stacks on top of an open
    // map popup, or the picker dropdown opens behind a still-closing QV.
    if (cur.qv && !req.qv) {
      var closeBtn = document.querySelector('.lf-qv-modal__close');
      if (closeBtn) closeBtn.click();
    }
    if (cur.mapPopup && !req.mapPopup) {
      var inst = getMapInstance();
      if (inst && inst.leafletMap) inst.leafletMap.closePopup();
      var backdrop = document.querySelector('.lf-bottom-sheet-backdrop');
      if (backdrop) backdrop.click();
    }
    if (cur.pickerOpen && !req.pickerOpen) {
      // Closing via the picker's docHandler — a click on something OUTSIDE
      // the picker triggers its outside-click close.
      document.body.click();
    }
    if (!mq.matches) {
      if (cur.mapExpanded !== !!req.mapExpanded) {
        var btn = document.querySelector('.lf-map-expand-btn');
        if (btn) btn.click();
      }
    } else {
      // Mobile: go through the toggle button rather than flipping the class
      // ourselves — the toggle handler also calls Leaflet's
      // `invalidateSize()` across several frames so the (previously hidden)
      // map gets its real dimensions and pin positions are correct. Without
      // it the map renders with stale 0x0 geometry and rings can't lock
      // onto an actual pin.
      if (cur.mapVisible !== !!req.mapVisible) {
        var mobileToggle = document.getElementById('lf-mobile-toggle');
        if (mobileToggle) mobileToggle.click();
      }
    }

    // Open-after-close: anything we still need that isn't already in place.
    if (!cur.pickerOpen && req.pickerOpen) {
      var pbtn = document.querySelector('.lf-toolbar .lf-cpick__btn');
      if (pbtn) pbtn.click();
    }
    if (!cur.qv && req.qv) {
      var card = findFirstCardWithMarketPrice();
      if (card) card.click();
    }
  }

  var WT_SLIDES = [
    {
      subtitle: 'Step 1 of 4',
      title: 'Where are you in the world?',
      body: 'Select where you would like the bag.\n\nThis will allow duties and taxes to be calculated should you choose to have the bag shipped to you.',
      initialTarget: '.lf-toolbar .lf-cpick',
      initialOpts: { padding: 14, shape: 'pill' },
      // Picker opens as part of the demo, not as a pre-state — that lets the
      // buyer see the open animation rather than walking into a state that's
      // already open.
      requires: {},
      // Skip the settleMs wait — the picker pill is in the toolbar which is
      // stable through any reconciler transition (map collapse on back-nav
      // from slide 2). The ring can start gliding immediately rather than
      // sitting still for 620ms before doing anything visible.
      skipSettle: true,

      script: function (api) {
        var pickerBtn = document.querySelector('.lf-toolbar .lf-cpick__btn');
        if (!pickerBtn) return;

        // Each beat is timed for the buyer to read what the ring is showing
        // them: pause → open → settle → ring detected row → linger → click
        // → settle → ring back to the pill so they see the chosen country.
        return api.delay(1100)
          .then(function () {
            if (!document.querySelector('.lf-toolbar .lf-cpick.is-open')) {
              pickerBtn.click();
            }
            return api.delay(800);
          })
          .then(function () {
            var detectedOpt = document.querySelector('.lf-toolbar .lf-cpick.is-open .lf-cpick__option');
            if (detectedOpt) api.pointAt(detectedOpt, { padding: 6, shape: 'rect' });
            return api.delay(1500);
          })
          .then(function () {
            // "Select" it — fires onChange, which re-syncs the button label
            // and closes the dropdown. Immediately glide the ring back to
            // the picker pill so it doesn't sit on the (now-invisible)
            // dropdown row while the close animation runs.
            var detectedOpt = document.querySelector('.lf-toolbar .lf-cpick.is-open .lf-cpick__option');
            if (detectedOpt) detectedOpt.click();
            api.pointAt('.lf-toolbar .lf-cpick', { padding: 14, shape: 'pill' });
            return api.delay(700);
          });
      }
    },

    {
      subtitle: 'Step 2 of 4',
      title: 'Find a bag on the map',
      body: 'Bags are located all over the world and can be collected locally or shipped to you.\n\nA red dot means duty and taxes to get the bag to you will be high.\n\nA green dot means there is no charge.',
      // Don't use #lf-map directly: during the panel's expand transition
      // the map's bounding box slides leftward (the desktop CSS animates
      // `left` from -27.5vw to -55vw, so the map's geometric centre
      // shifts from page 77.5vw to page 50vw over ~500ms). Targeting the
      // live bbox makes the ring chase the moving map and produces a
      // visible zig-zag. Instead, compute the *post-expand* centre from
      // the layout (it's deterministic) and ring there from the start —
      // the ring's CSS transition then runs in sync with the expand,
      // ending exactly where the map ends up.
      initialTargetResolver: function () { return predictedExpandedMapCenter(); },
      initialOpts: { size: 180, shape: 'circle' },
      requires: { mapExpanded: true, mapVisible: true },
      // Skip the settleMs wait so the script (which triggers the camera
      // flyTo) runs immediately, in parallel with the panel's expand and
      // the ring's CSS glide. All three converge on the same predicted
      // point at the same time — no "ring at centre, wait, pin appears"
      // sequence.
      skipSettle: true,

      script: function (api) {
        var inst = getMapInstance();
        if (!inst || !inst.leafletMap) return;

        // Kick off the camera pan to the green pin RIGHT NOW so it
        // animates in parallel with (a) the panel's CSS expand and (b)
        // the ring's CSS transition to the predicted post-expand
        // centre. Because flyTo centres the pin on the map container,
        // and the container's centre after expand equals the predicted
        // point the ring is already heading to, all three motions meet
        // at the same spot at the same time.
        var pin = inst.findGreenPin && inst.findGreenPin();
        if (pin) {
          var m = inst.leafletMap;
          // Force Leaflet to re-read its container size first. On mobile
          // the panel was display:none until the reconciler activated
          // it, so Leaflet's internal map dimensions are stale —
          // without this the flyTo would target the pre-expand viewport
          // and the pin would land off-centre when the panel finishes
          // opening.
          try { m.invalidateSize({ animate: false, pan: false }); } catch (e) {}
          m.flyTo([pin.lat, pin.lng], Math.max(m.getZoom(), 4), {
            duration: 0.7,
            easeLinearity: 0.3
          });
        }

        // Wait for ring + panel + flyTo to all converge (~700ms each
        // running concurrently), then pixel-lock the ring onto the pin
        // element. Shrinks the 180px circle down to the pin's natural
        // bbox.
        return api.delay(950)
          .then(function () {
            var pin2 = inst.findGreenPin && inst.findGreenPin();
            if (pin2 && pin2.el) api.pointAt(pin2.el, { padding: 12, shape: 'circle' });
            return api.delay(700);
          })
          .then(function () {
            // Use the map's own helper, which calls Leaflet's
            // `marker.fire('click')` — that's the same code path a real
            // tap takes and isn't subject to the iOS Safari quirk where a
            // DOM-dispatched synthetic click sometimes never reaches
            // Leaflet's wrapper listener. Opens a Leaflet popup on
            // desktop, or the bottom sheet on mobile.
            if (inst.triggerSamplePin) inst.triggerSamplePin();
            var isMobile = window.matchMedia('(max-width: 999px)').matches;
            return api.delay(isMobile ? 700 : 550);
          })
          .then(function () {
            var popup = document.querySelector('.lf-map-popup')
                     || document.querySelector('.lf-bottom-sheet.is-open');
            if (popup) {
              api.pointAt(popup, { padding: 10, shape: 'rect' });
            } else {
              var pin = inst.findGreenPin && inst.findGreenPin();
              if (pin && pin.el) api.pointAt(pin.el, { padding: 12, shape: 'circle' });
            }
          });
      }
    },

    {
      subtitle: 'Step 3 of 4',
      title: 'Compare against the market',
      body: 'Each bag listed shows the Lilac Blue Drop price as well as the usual resale market price.',
      // Initial ring lands inside the (already-open) QV on the price block.
      initialTargetResolver: function () {
        var priceRow = document.querySelector('.lf-qv-overlay.is-open .lf-qv__price-row');
        if (priceRow) return priceRow;
        var card = findFirstCardWithMarketPrice();
        return card ? (card.querySelector('.lf-card__info') || card) : null;
      },
      initialOpts: { padding: 10, shape: 'rect' },
      requires: { qv: true },

      script: function (api) {
        // QV is already open via reconcile — just wait for its slide-in
        // animation (~220ms) to land before ringing the comparison.
        return api.delay(280)
          .then(function () {
            var priceRow    = document.querySelector('.lf-qv-overlay.is-open .lf-qv__price-row');
            var marketPrice = document.querySelector('.lf-qv-overlay.is-open .lf-qv__market-price');
            var target = marketPrice && priceRow ? wrapRange(priceRow, marketPrice) : (marketPrice || priceRow);
            if (target) api.pointAt(target, { padding: 10, shape: 'rect' });
          });
      }
    },

    {
      subtitle: 'Step 4 of 4',
      title: 'Reserve with a deposit',
      body: 'Choose your delivery option — will you collect the bag or have it delivered?\n\nA £500 non-refundable deposit holds a bag at The Drop price for three days. During those three days, the remainder must be paid by bank transfer. Payment can be made in multiple currencies.',
      initialTargetResolver: function () {
        return document.querySelector('.lf-qv-overlay.is-open .lf-qv__btn-cart')
            || document.querySelector('.lf-header__cart');
      },
      initialOpts: { padding: 10, shape: 'pill' },
      requires: { qv: true },

      script: function (api) {
        return api.delay(280)
          .then(function () {
            var btn = document.querySelector('.lf-qv-overlay.is-open .lf-qv__btn-cart');
            if (btn) {
              try { btn.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { btn.scrollIntoView(); }
              return api.delay(280).then(function () {
                api.pointAt(btn, { padding: 10, shape: 'pill' });
              });
            }
            api.pointAt('.lf-header__cart', { padding: 8, shape: 'circle' });
          });
      }
    }
  ];

  // Returns a phantom element pinned at the *post-expand* geometric centre
  // of the map panel. Used by slide 2 so the ring's CSS transition aims
  // at a fixed point rather than chasing the live #lf-map bbox while the
  // panel is mid-flight. Coordinates come straight from the CSS layout:
  //   • desktop  — expanded map has `left:-55vw; width:100vw` inside the
  //     45vw right pane (which starts at page 55vw), so its centre lands
  //     at page 50vw.
  //   • mobile   — `.lf-split__map` becomes a position:fixed full-screen
  //     panel below the header, so the centre is (vw/2, header→bottom
  //     midpoint).
  // Both formulae reduce to the same point: (vw/2, top+(vh-top)/2).
  function predictedExpandedMapCenter() {
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var topRaw = getComputedStyle(document.documentElement).getPropertyValue('--lf-top-offset');
    var topOffset = parseFloat(topRaw) || 0;
    var cx = vw / 2;
    var cy = topOffset + (vh - topOffset) / 2;
    var phantom = document.createElement('div');
    phantom.style.cssText = 'position:fixed;pointer-events:none;left:' + cx + 'px;top:' + cy + 'px;width:0;height:0;';
    document.body.appendChild(phantom);
    requestAnimationFrame(function () { setTimeout(function () { phantom.remove(); }, 2000); });
    return phantom;
  }

  // Returns a synthetic bounding box that encloses both `a` and `b`. Used
  // when we want a single ring to wrap two adjacent rows (e.g. the price
  // and the market-price line in the quick view).
  function wrapRange(a, b) {
    var ra = a.getBoundingClientRect();
    var rb = b.getBoundingClientRect();
    var left   = Math.min(ra.left,   rb.left);
    var top    = Math.min(ra.top,    rb.top);
    var right  = Math.max(ra.right,  rb.right);
    var bottom = Math.max(ra.bottom, rb.bottom);
    // Build a phantom DOM element positioned at the union so the existing
    // ring positioning code can treat it like any other target.
    var phantom = document.createElement('div');
    phantom.style.cssText = 'position:fixed;pointer-events:none;left:' + left + 'px;top:' + top +
      'px;width:' + (right - left) + 'px;height:' + (bottom - top) + 'px;';
    document.body.appendChild(phantom);
    // Auto-cleanup the phantom on the next animation frame after the ring
    // has measured it.
    requestAnimationFrame(function () { setTimeout(function () { phantom.remove(); }, 1200); });
    return phantom;
  }

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) { return null; }
  }

  function writeCookie(name, value, days) {
    try {
      var expires = '';
      if (days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 86400000);
        expires = '; expires=' + d.toUTCString();
      }
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    } catch (e) { /* noop */ }
  }

  function initWalkthrough() {
    var overlay = document.getElementById('lf-wt-overlay');
    if (!overlay) return;

    var ring     = overlay.querySelector('#lf-wt-ring');
    var card     = overlay.querySelector('#lf-wt-card');
    var numEl    = overlay.querySelector('#lf-wt-num');
    var totalEl  = overlay.querySelector('#lf-wt-total');
    var subEl    = overlay.querySelector('#lf-wt-subtitle');
    var titleEl  = overlay.querySelector('#lf-wt-title');
    var bodyEl   = overlay.querySelector('#lf-wt-body');
    var dotsEl   = overlay.querySelector('#lf-wt-dots');
    var backBtn  = overlay.querySelector('[data-wt-back]');
    var nextBtn  = overlay.querySelector('[data-wt-next]');
    var doneBtn  = overlay.querySelector('[data-wt-done]');
    var closeBtn = overlay.querySelector('[data-wt-close]');

    if (!ring || !card || !numEl || !subEl || !titleEl || !bodyEl) return;

    var idx = 0;
    var isOpen = false;
    var resizeRaf = null;
    var sceneToken = 0;                 // bumped on every slide change
    var lastRingOpts = {};              // remembered so resize re-applies the same shape
    var lastRingTarget = null;
    var mobileMq = window.matchMedia('(max-width: 999px)');

    // Build the progress dots once. They mirror the slide count.
    if (dotsEl) {
      dotsEl.innerHTML = '';
      for (var i = 0; i < WT_SLIDES.length; i++) {
        var dot = document.createElement('span');
        dot.className = 'lf-wt-card__dot';
        dotsEl.appendChild(dot);
      }
    }
    if (totalEl) totalEl.textContent = 'of ' + WT_SLIDES.length;

    function resolveTarget(target) {
      if (!target) return null;
      if (typeof target === 'string') return document.querySelector(target);
      return target; // already an element
    }

    function resolveInitialTarget(slide) {
      if (typeof slide.initialTargetResolver === 'function') {
        try { var r = slide.initialTargetResolver(); if (r) return r; } catch (e) { /* noop */ }
      }
      if (slide.initialTarget) return resolveTarget(slide.initialTarget);
      return null;
    }

    function positionRing(target, opts) {
      opts = opts || {};
      lastRingOpts = opts;
      lastRingTarget = target;

      if (!target) {
        // No target → centre a small ring on screen so the overlay still
        // looks intentional rather than empty.
        var cx = window.innerWidth / 2;
        var cy = window.innerHeight / 2;
        ring.style.width = '120px';
        ring.style.height = '120px';
        ring.style.left = (cx - 60) + 'px';
        ring.style.top = (cy - 60) + 'px';
        ring.style.borderRadius = '50%';
        positionCard(cx - 60, cy - 60, 120, 120);
        return;
      }

      var rect = target.getBoundingClientRect();
      var pad = opts.padding != null ? opts.padding : 12;
      var shape = opts.shape || 'circle';

      var x, y, w, h, radius;
      // `size` overrides the natural bbox sizing — handy when the target is
      // a huge container (e.g. the whole map) and we want a tidy ring
      // centred on it rather than one that wraps the entire element.
      if (opts.size) {
        var s = opts.size;
        var fcx = rect.left + rect.width / 2;
        var fcy = rect.top + rect.height / 2;
        x = fcx - s / 2;
        y = fcy - s / 2;
        w = s;
        h = s;
        ring.style.borderRadius = shape === 'circle' ? '50%' : (shape === 'pill' ? (s / 2) + 'px' : '16px');
      } else if (shape === 'circle') {
        radius = Math.max(rect.width, rect.height) / 2 + pad;
        var ccx = rect.left + rect.width / 2;
        var ccy = rect.top + rect.height / 2;
        x = ccx - radius;
        y = ccy - radius;
        w = radius * 2;
        h = radius * 2;
        ring.style.borderRadius = '50%';
      } else if (shape === 'pill') {
        x = rect.left - pad;
        y = rect.top - pad;
        w = rect.width + pad * 2;
        h = rect.height + pad * 2;
        ring.style.borderRadius = (h / 2) + 'px';
      } else {
        x = rect.left - pad;
        y = rect.top - pad;
        w = rect.width + pad * 2;
        h = rect.height + pad * 2;
        ring.style.borderRadius = '16px';
      }

      ring.style.left   = x + 'px';
      ring.style.top    = y + 'px';
      ring.style.width  = w + 'px';
      ring.style.height = h + 'px';

      positionCard(x, y, w, h);
    }

    function positionCard(rx, ry, rw, rh) {
      // Place the card outside the ring, preferring below → above → right →
      // left, then clamp to the viewport so it never spills off-screen.
      // On narrow viewports the card pins to the bottom of the screen as a
      // sheet so it doesn't fight the ring for space.
      var vw = window.innerWidth;
      var vh = window.innerHeight;

      // Mobile: pin as a sheet at the opposite end of the viewport from the
      // ring. If the ring is in the top half, sheet sits at the bottom; if
      // the ring is in the bottom half, sheet flips to the top. Keeps the
      // walkthrough card from ever covering whatever it's pointing at —
      // matters most for slide 4's Reserve button (low in the QV scroll)
      // and slide 2's pin if the map flies it south of centre.
      if (mobileMq.matches) {
        card.style.left = '';
        card.style.top  = '';
        card.style.bottom = '';
        var ringMidY = ry + rh / 2;
        var topHalf = ringMidY < window.innerHeight / 2;
        card.classList.toggle('lf-wt-card--sheet-bottom', topHalf);
        card.classList.toggle('lf-wt-card--sheet-top',   !topHalf);
        card.classList.add('lf-wt-card--sheet');
        return;
      }
      card.classList.remove('lf-wt-card--sheet');
      card.classList.remove('lf-wt-card--sheet-top');
      card.classList.remove('lf-wt-card--sheet-bottom');

      // Measure the card. It's already visible (overlay is open) so this
      // returns its final laid-out size.
      var cardRect = card.getBoundingClientRect();
      var cw = cardRect.width || 320;
      var ch = cardRect.height || 200;
      var GAP = 18;
      var MARGIN = 16;

      var cardLeft, cardTop;

      var spaceBelow = vh - (ry + rh);
      var spaceAbove = ry;
      var spaceRight = vw - (rx + rw);
      var spaceLeft  = rx;

      if (spaceBelow >= ch + GAP + MARGIN) {
        // Below — centre on ring horizontally.
        cardLeft = rx + rw / 2 - cw / 2;
        cardTop  = ry + rh + GAP;
      } else if (spaceAbove >= ch + GAP + MARGIN) {
        cardLeft = rx + rw / 2 - cw / 2;
        cardTop  = ry - ch - GAP;
      } else if (spaceRight >= cw + GAP + MARGIN) {
        cardLeft = rx + rw + GAP;
        cardTop  = ry + rh / 2 - ch / 2;
      } else if (spaceLeft >= cw + GAP + MARGIN) {
        cardLeft = rx - cw - GAP;
        cardTop  = ry + rh / 2 - ch / 2;
      } else {
        // No room anywhere clean — fall back to bottom-right corner.
        cardLeft = vw - cw - MARGIN;
        cardTop  = vh - ch - MARGIN;
      }

      cardLeft = Math.max(MARGIN, Math.min(cardLeft, vw - cw - MARGIN));
      cardTop  = Math.max(MARGIN, Math.min(cardTop,  vh - ch - MARGIN));

      card.style.left = cardLeft + 'px';
      card.style.top  = cardTop  + 'px';
    }

    function ensureVisible(target) {
      if (!target) return Promise.resolve();
      var rect = target.getBoundingClientRect();
      var fullyOnScreen =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth;
      if (fullyOnScreen) return Promise.resolve();
      try {
        target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
      } catch (e) {
        target.scrollIntoView();
      }
      // Wait for the smooth scroll to settle before measuring.
      return new Promise(function (resolve) { setTimeout(resolve, 360); });
    }

    // Build the per-scene api passed to slide scripts. `delay` only resolves
    // if the slide is still the active one — that short-circuits any
    // in-flight chains when the user navigates away mid-animation.
    function makeSceneApi(myToken) {
      function isAlive() { return isOpen && sceneToken === myToken; }
      return {
        isAlive: isAlive,
        pointAt: function (target, opts) {
          if (!isAlive()) return;
          var el = resolveTarget(target);
          if (el) positionRing(el, opts || {});
        },
        delay: function (ms) {
          return new Promise(function (resolve) {
            setTimeout(function () { if (isAlive()) resolve(); /* else: drop the chain */ }, ms);
          });
        }
      };
    }

    function showSlide(i) {
      if (i < 0) i = 0;
      if (i > WT_SLIDES.length - 1) i = WT_SLIDES.length - 1;

      idx = i;
      sceneToken++;
      var myToken = sceneToken;

      var slide = WT_SLIDES[idx];

      // Snapshot state BEFORE reconcile so we can tell whether the
      // reconciler is about to kick off a transition (QV slide-in, map
      // expand) — those need a settle delay before the ring measures.
      var preReconcile = getCurrentWtState();

      // Bring the page to the state this slide needs (close any QV/popup
      // that the previous slide had open, open the ones this slide needs).
      // Reconcile only flips what differs, so 3 ↔ 4 doesn't re-render the
      // already-open QV.
      reconcileWtState(slide.requires || {});

      // Populate card content first so the next layout pass has its final
      // height, which positionCard() needs to place it correctly.
      numEl.textContent = String(idx + 1);
      subEl.textContent = slide.subtitle || '';
      titleEl.textContent = slide.title || '';
      bodyEl.textContent = slide.body || '';

      if (backBtn) backBtn.hidden = idx === 0;
      var isLast = idx === WT_SLIDES.length - 1;
      if (nextBtn) nextBtn.hidden = isLast;
      if (doneBtn) doneBtn.hidden = !isLast;

      if (dotsEl) {
        var dots = dotsEl.children;
        for (var d = 0; d < dots.length; d++) {
          dots[d].classList.toggle('is-active', d === idx);
        }
      }

      // Give the reconciler's transitions a beat to settle (QV slide-in,
      // map expand) before measuring the initial target. Without this, the
      // ring lands on the previous-layout position of an element that's
      // still animating into place.
      var req = slide.requires || {};
      var qvFlipped       = !!req.qv          !== preReconcile.qv;
      var mapFlipped      = (!!req.mapExpanded !== preReconcile.mapExpanded) ||
                            (!!req.mapVisible  !== preReconcile.mapVisible);
      // Map expand/collapse runs ~500ms in CSS plus Leaflet's invalidateSize
      // on the trailing edge. On mobile the toggle's wake-up sequence does
      // an invalidate at +600ms on first activation (the panel was display:
      // none until then), so we need to wait past that or the ring lands
      // on stale pin coordinates. QV slide-in is faster (~220ms).
      var mobileMap = mobileMq.matches;
      // Slides that drive their own choreography in parallel with the
      // reconciler transition opt out of the settle wait via
      // `skipSettle: true` — their script runs immediately and is
      // expected to handle any timing internally.
      var settleMs = slide.skipSettle
        ? 0
        : (mapFlipped ? (mobileMap ? 760 : 620) : (qvFlipped ? 280 : 0));

      // Early-pass reposition: if there's a settle delay (map expand / QV
      // slide-in), don't let the ring sit frozen on the previous slide's
      // target for hundreds of ms before jumping. As soon as the reconciler
      // has applied its class changes, try to position the ring at the new
      // initial target. The ring's CSS transition will glide it during the
      // same window the layout is settling, so the overall motion reads as
      // one smooth move instead of "stall, then snap". We skip the early
      // pass if the target has a zero-size bbox (e.g. mobile map still
      // display:none mid-toggle) — in that case the freeze-then-move is
      // unavoidable and `go()` will handle the final position.
      if (settleMs) {
        requestAnimationFrame(function () {
          if (sceneToken !== myToken) return;
          var earlyTarget = resolveInitialTarget(slide);
          if (!earlyTarget) return;
          var r = earlyTarget.getBoundingClientRect();
          if (r.width === 0 && r.height === 0) return;
          positionRing(earlyTarget, slide.initialOpts || {});
        });
      }

      function go() {
        if (sceneToken !== myToken) return;
        var initialTarget = resolveInitialTarget(slide);
        ensureVisible(initialTarget).then(function () {
          if (sceneToken !== myToken) return;
          initialTarget = resolveInitialTarget(slide);
          positionRing(initialTarget, slide.initialOpts || {});
          if (typeof slide.script === 'function') {
            try { slide.script(makeSceneApi(myToken)); } catch (e) { /* noop */ }
          }
        });
      }
      if (settleMs) setTimeout(go, settleMs); else go();
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lf-wt-open');
      // Two-frame delay so the overlay's fade-in transition kicks in before
      // we measure the card (which needs its final laid-out size).
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { showSlide(0); });
      });
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lf-wt-open');
      sceneToken++;                       // invalidate any pending delays
      // Reconcile to "nothing required" — closes any QV/popup/picker that
      // the buyer happened to be on.
      reconcileWtState({});
      writeCookie(WT_COOKIE, '1', 365);
    }

    function next() { idx < WT_SLIDES.length - 1 ? showSlide(idx + 1) : close(); }
    function back() { if (idx > 0) showSlide(idx - 1); }

    if (nextBtn)  nextBtn.addEventListener('click', next);
    if (doneBtn)  doneBtn.addEventListener('click', close);
    if (backBtn)  backBtn.addEventListener('click', back);
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Note: no click-to-dismiss on the dim area. A click on the page during
    // a scripted slide would land on the overlay (clicks through the ring's
    // `pointer-events: none` resolve to the overlay's hit-test), and we
    // don't want the buyer to lose the walkthrough by clicking on something
    // the ring is pointing at. Close affordances are the X button, ESC,
    // and "Got it" on the last slide.

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape')      close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft')  back();
    });

    // Re-measure on resize / orientation change. rAF-debounced so we don't
    // thrash layout while the user drags a desktop window edge.
    function scheduleReposition() {
      if (!isOpen) return;
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        if (lastRingTarget && document.contains(lastRingTarget)) {
          positionRing(lastRingTarget, lastRingOpts);
        } else {
          var slide = WT_SLIDES[idx];
          var t = resolveInitialTarget(slide);
          positionRing(t, slide.initialOpts || {});
        }
      });
    }
    window.addEventListener('resize', scheduleReposition);
    window.addEventListener('scroll', scheduleReposition, { passive: true });

    // Re-openable from the "What is the Trading Floor?" explain modal. The
    // launch button closes the explain modal first (it sits on top of the
    // walkthrough overlay in stacking terms; leaving it open would block
    // the ring's pointer-events).
    var launchers = document.querySelectorAll('[data-wt-launch]');
    launchers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var explain = document.getElementById('lf-explain-overlay');
        if (explain && !explain.hidden) {
          explain.hidden = true;
          document.body.style.overflow = '';
        }
        // Wait one frame for the explain modal to actually unmount before
        // opening the walkthrough so its transition out doesn't fight ours.
        requestAnimationFrame(function () { open(); });
      });
    });

    // First-visit auto-open. Cookie set on close so every fresh browser
    // sees it exactly once; subsequent visits stay quiet.
    if (!readCookie(WT_COOKIE)) {
      // Small delay so the page (map tiles, country picker, cards) has
      // settled before we point at things.
      setTimeout(open, 700);
    }
  }

  function init() {
    initCustomsDuty();
    initQuickView();
    initHotbagPdp();

    var products = extractProducts();
    mapInstance = createMap('lf-map', products);
    if (selectedDestISO && mapInstance) mapInstance.updatePinColors(selectedDestISO);
    initCardHoverLinking(products, mapInstance);
    initMobileToggle(mapInstance);
    initMapExpandToggle(mapInstance);
    initFiltersPanel();
    initToolbarCollapse();
    initColumnClip();
    initQvDeepLink();
    initExplainOverlay();
    initAiDisclaimer();
    initWalkthrough();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
